import React, { useState, useEffect, useRef, useCallback, useLayoutEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { AdminTopNav, AdminSideNav } from '../administrationNavigation/AdminConsoleNavigation';
import GetCookie from '../../utils/GetCookie';
import ErrorPopup from '../../utils/popup/ErrorPopup';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import TrimContent from '../../utils/TrimContent';
import '../../utils/smartTooltip';
import PaginationControls from '../../utils/PaginationControls';
import { useTimezone } from '../../utils/TimezoneContext';
import ThemedSelect from '../../utils/ThemedSelect';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../../../styles/LlmTraces.css';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'SUCCESS', label: 'Success' },
  { value: 'ERROR', label: 'Error' },
  { value: 'RUNNING', label: 'Running' }
];

// "Workflow" in the UI, llm_run.pipeline on the data model. These are the
// exact strings the tracer writes today — genbi_agent_executor passes
// pipeline=f"genbi_report_{mode}", with mode from get_execution_mode()
// ("template" | "single" | "selective"). GenBI agent execution is the only
// instrumented path so far; a newly instrumented pipeline needs an entry here.
const WORKFLOW_OPTIONS = [
  { value: '', label: 'All workflows' },
  { value: 'genbi_report_template', label: 'HTA Studio — Full preset' },
  { value: 'genbi_report_selective', label: 'HTA Studio — Selected agents' },
  { value: 'genbi_report_single', label: 'HTA Studio — Single agent' },
  // Apply Writing Rules button (consumers country_rules/writing_rules.py)
  { value: 'genbi_writing_rules', label: 'HTA Studio — Language writing rules' }
];

// Chart-only control, separate from the filter bar above: it sets how the
// backend buckets calls-over-time, not which rows are included. The backend
// only accepts "hour" | "day" (a Literal on the request model — anything else
// is a 422), so there is no week option.
const GRANULARITY_OPTIONS = [
  { value: 'hour', label: 'By hour' },
  { value: 'day', label: 'By day' }
];

// Plain number/percentage formatters mirroring AdminDashboard's numberLabel:
// an em-dash placeholder while a value is null (still loading, or the
// endpoint failed) rather than showing "0" and implying there really is none.
const formatNumber = (value) => (value === null || value === undefined ? '—' : Number(value).toLocaleString());

// The backend sends error_rate already as a percentage (0–100, rounded to 2dp),
// not a 0–1 fraction.
const formatErrorRate = (value) => (value === null || value === undefined ? '—' : `${Number(value).toFixed(1)}%`);

// Input/output/metadata can arrive as an object, an already-serialized JSON
// string, or be entirely absent (a RUNNING row, or a detail call that failed
// against a backend that doesn't exist yet) — this never throws either way.
const formatJson = (value) => {
  if (value === undefined || value === null) return 'No data available.';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch (err) {
    return String(value);
  }
};

const statusBadgeClass = (status) => {
  if (status === 'SUCCESS') return 'ev-message-card3-green';
  if (status === 'ERROR') return 'ev-message-card1 text-danger';
  return 'ev-message-card1';
};

const statusLabel = (status) => {
  if (status === 'SUCCESS') return 'Success';
  if (status === 'ERROR') return 'Error';
  if (status === 'RUNNING') return 'Running';
  return status || 'Unknown';
};

// step_type is a lowercase snake_case string from the tracer
// ("tool_call", "embedding_call", ...); "llm_call" -> "Llm call".
const stepTypeLabel = (stepType) => {
  if (!stepType) return 'Step';
  const text = String(stepType).replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

// Readable names for the tracer's step types (llm_tracer.py). Anything not
// listed falls back to the humanised raw type rather than being hidden.
const LLM_STEP_META = {
  context_modification: { label: 'Query rewrite', icon: 'bi-pencil-square' },
  dataframe_code_gen: { label: 'Data analysis code', icon: 'bi-code-slash' },
  chain: { label: 'Answer generation', icon: 'bi-chat-left-text' },
  image_analysis_call: { label: 'Image description', icon: 'bi-image' }
};
// tool_call steps carry the actual tool in tool_name — the values
// recursive_cot_agent passes to trace_tool_call.
const TOOL_STEP_META = {
  document_search: { label: 'Document search', icon: 'bi-search' },
  df_analysis: { label: 'Data analysis', icon: 'bi-table' },
  image_analysis: { label: 'Image analysis', icon: 'bi-images' }
};
const NON_LLM_STEP_TYPES = new Set(['tool_call', 'embedding_call']);
const isLlmStep = (s) => !!s.step_type && !NON_LLM_STEP_TYPES.has(s.step_type);

const stepDisplay = (s) => {
  if (s.step_type === 'tool_call') {
    return TOOL_STEP_META[s.tool_name]
      || { label: s.tool_name ? stepTypeLabel(s.tool_name) : 'Tool call', icon: 'bi-tools' };
  }
  if (s.step_type === 'embedding_call') return { label: 'Embedding', icon: 'bi-diagram-2' };
  const meta = LLM_STEP_META[s.step_type] || { label: stepTypeLabel(s.step_type), icon: 'bi-cpu' };
  if (s.step_type === 'chain' && s.chain_number) {
    return { ...meta, label: `${meta.label} (${s.chain_number}${s.chain_total ? `/${s.chain_total}` : ''})` };
  }
  return meta;
};

// "us.anthropic.claude-sonnet-4-6" -> "claude-sonnet-4-6"; the full id stays
// in the tooltip.
const shortModelId = (modelId) => (modelId ? String(modelId).split('.').pop() : '');

// 850 -> "850 ms", 25281 -> "25.3s", 90282 -> "1m 30s"
const formatDuration = (ms) => {
  if (ms === null || ms === undefined) return '—';
  const n = Number(ms);
  if (n < 1000) return `${n} ms`;
  if (n < 60000) return `${(n / 1000).toFixed(1)}s`;
  const totalSec = Math.round(n / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const sec = totalSec % 60;
  return h ? `${h}h ${m}m` : `${m}m ${sec}s`;
};

// Offset of a step from the start of its run: 0 -> "+0.0s", 13200 -> "+13.2s",
// 65000 -> "+1m 05s".
const formatOffset = (ms) => {
  const n = Math.max(Number(ms) || 0, 0);
  if (n < 60000) return `+${(n / 1000).toFixed(1)}s`;
  const totalSec = Math.round(n / 1000);
  return `+${Math.floor(totalSec / 60)}m ${String(totalSec % 60).padStart(2, '0')}s`;
};

// ── Run metadata ────────────────────────────────────────────────────────
// run_metadata is what genbi_agent_executor passes to start_agent_run:
// section_id, template_id, feedback_used, amend_response, preserve_response.
// Mode follows the pool manager's own rule: amend_response -> Amend,
// preserve_response -> Extend, neither -> a fresh (normal) generation.
const runModeLabel = (meta) => {
  if (meta.amend_response) return 'Amend';
  if (meta.preserve_response) return 'Extend';
  return 'Normal';
};
const KNOWN_RUN_METADATA_KEYS = new Set([
  'section_id', 'template_id', 'feedback_used', 'amend_response', 'preserve_response'
]);

// ── Readable step content ───────────────────────────────────────────────
// Prompt templates are written inside indented Python strings, and the
// user's own text is spliced in unindented, so there is no common indent to
// strip — the template lines just carry a run of leading spaces. Leading
// whitespace is dropped per line, except inside ``` code fences where it is
// meaningful. Runs of blank lines collapse to one.
const cleanPromptText = (text) => {
  let inFence = false;
  const out = [];
  String(text).replace(/\r\n/g, '\n').split('\n').forEach((line) => {
    if (line.trim().startsWith('```')) inFence = !inFence;
    const cleaned = inFence ? line : line.trimStart();
    if (!cleaned.trim() && out.length && !out[out.length - 1].trim()) return;
    out.push(cleaned);
  });
  while (out.length && !out[0].trim()) out.shift();
  while (out.length && !out[out.length - 1].trim()) out.pop();
  return out.join('\n');
};

// A message's content is a string, or (multimodal) a list of content blocks.
const contentToText = (content) => {
  if (content === null || content === undefined) return '';
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content.map((block) => {
      if (typeof block === 'string') return block;
      if (block && block.type === 'text') return block.text || '';
      if (block && (block.type === 'image' || block.type === 'image_url')) return '[image]';
      return JSON.stringify(block);
    }).join('\n');
  }
  return JSON.stringify(content, null, 2);
};

// LangChain message types as the tracer records them (_message_to_dict).
const ROLE_LABELS = { human: 'User', system: 'System', ai: 'Assistant', tool: 'Tool' };

// Model outputs wrap their answer in XML-style tags
// (<MODIFIED_CONTEXT>...</MODIFIED_CONTEXT>, <DATA_REQUIRED>...). Split those
// out into labelled sections; anything outside a tag stays plain text.
const splitTaggedSections = (text) => {
  const parts = [];
  const re = /<([A-Z][A-Z0-9_]*)>([\s\S]*?)<\/\1>/g;
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push({ text: text.slice(last, m.index) });
    parts.push({ tag: m[1], text: m[2] });
    last = re.lastIndex;
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts.filter((p) => p.tag || p.text.trim());
};
const tagLabel = (tag) => {
  const t = tag.toLowerCase().replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
};

const COLLAPSED_LINES = 12;

// Long text shows its first lines with a Show more toggle.
const CollapsibleText = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const clean = cleanPromptText(text);
  const lines = clean.split('\n');
  const long = lines.length > COLLAPSED_LINES || clean.length > 1500;
  const shown = !long || expanded ? clean : lines.slice(0, COLLAPSED_LINES).join('\n').slice(0, 1500);
  return (
    <div>
      <div className="llm-td-text" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {shown}{long && !expanded ? '…' : ''}
      </div>
      {long && (
        <button
          type="button"
          className="btn p-0 mt-1 ev-fs-12 ev-link-accent"
          onClick={(e) => { e.preventDefault(); setExpanded((v) => !v); }}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
};

// Only model *output* is split on tags. Prompts often contain the same tags
// as format instructions ("<MODIFIED_CONTEXT>modified context here</...>"),
// and turning those into sections made the instructions read like results.
const TextWithSections = ({ text }) => (
  <>
    {splitTaggedSections(String(text)).map((part, i) => (
      part.tag ? (
        <div key={i} className="mb-2">
          <div className="llm-td-section">{tagLabel(part.tag)}</div>
          <CollapsibleText text={part.text} />
        </div>
      ) : (
        <div key={i} className="mb-2"><CollapsibleText text={part.text} /></div>
      )
    ))}
  </>
);

const MessageBubble = ({ role, children }) => (
  <div className="mb-2">
    <div className="llm-td-role">{role}</div>
    <div className="llm-td-bubble">{children}</div>
  </div>
);

// Tool inputs are small dicts (query, doc_ids, ...): one line per key.
const KeyValueList = ({ value }) => (
  <div className="llm-td-text">
    {Object.entries(value).map(([k, v]) => (
      <div key={k} className="d-flex gap-2 mb-1">
        <span className="ev-light-reg text-nowrap">{stepTypeLabel(k)}</span>
        <span style={{ wordBreak: 'break-word' }}>
          {typeof v === 'string' ? v : Array.isArray(v) ? `${v.length} item${v.length === 1 ? '' : 's'}: ${v.join(', ')}` : JSON.stringify(v)}
        </span>
      </div>
    ))}
  </div>
);

const TruncatedNote = ({ value }) => (
  <p className="ev-fs-12 ev-light-reg mb-1">
    Too large to store in full ({formatNumber(value.original_len)} characters) — showing the start only.
  </p>
);

// Renders one side (input or output) of a step readably, falling back to
// the raw JSON for any shape it doesn't recognise.
const StepSide = ({ step, side }) => {
  const value = step[side];
  if (value === null || value === undefined) return <p className="ev-fs-12 ev-light-reg mb-0">No data recorded.</p>;
  if (value && value._truncated) {
    return (<><TruncatedNote value={value} /><CollapsibleText text={value.preview} /></>);
  }
  const llm = isLlmStep(step);
  if (llm && side === 'input' && Array.isArray(value.prompts)) {
    // Chat models: prompts is a list of message batches; plain completion
    // models: a list of prompt strings.
    return value.prompts.flatMap((batch, bi) => (Array.isArray(batch) ? batch : [batch]).map((msg, mi) => {
      const isObj = msg && typeof msg === 'object';
      const role = isObj ? (ROLE_LABELS[msg.type] || stepTypeLabel(msg.type || 'message')) : 'User';
      return (
        <MessageBubble key={`${bi}-${mi}`} role={role}>
          <CollapsibleText text={contentToText(isObj ? msg.content : msg)} />
        </MessageBubble>
      );
    }));
  }
  if (llm && side === 'input' && value.prompt) {
    // Raw invoke_model calls (image description): {s3_path, prompt}.
    return (
      <MessageBubble role="User">
        {value.s3_path && <p className="ev-fs-12 ev-light-reg mb-1">Image: {value.s3_path}</p>}
        <CollapsibleText text={contentToText(value.prompt)} />
      </MessageBubble>
    );
  }
  if (llm && side === 'output') {
    const outputs = Array.isArray(value) ? value : [value];
    return outputs.map((o, i) => (
      <MessageBubble key={i} role="Assistant">
        <TextWithSections text={contentToText(o)} />
      </MessageBubble>
    ));
  }
  if (!llm && side === 'input' && typeof value === 'object' && !Array.isArray(value)) {
    return <KeyValueList value={value} />;
  }
  if (typeof value === 'string') return <CollapsibleText text={value} />;
  return <CollapsibleText text={JSON.stringify(value, null, 2)} />;
};

const StepContent = ({ step }) => {
  const [showRaw, setShowRaw] = useState(false);
  return (
    <div className="mt-2">
      {step.error_message && (
        <p className="ev-small-p-dark text-danger mb-2">{step.error_message}</p>
      )}
      <div className="d-flex justify-content-between align-items-center mb-1">
        <label className="ev-reg-p-muted mb-0">Input</label>
        <button
          type="button"
          className="btn p-0 ev-fs-12 ev-link-accent"
          onClick={(e) => { e.preventDefault(); setShowRaw((v) => !v); }}
        >
          {showRaw ? 'Readable view' : 'View raw'}
        </button>
      </div>
      {showRaw ? (
        <pre className="scrollable-element llm-td-raw mb-0" style={{ maxHeight: '320px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {formatJson(step.input)}
        </pre>
      ) : <StepSide step={step} side="input" />}
      <label className="ev-reg-p-muted mt-2 mb-1 d-block">Output</label>
      {showRaw ? (
        <pre className="scrollable-element llm-td-raw mb-0" style={{ maxHeight: '320px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {formatJson(step.output)}
        </pre>
      ) : <StepSide step={step} side="output" />}
    </div>
  );
};

// Step timestamps come from json.dumps(default=str) in the tracer, i.e.
// "2026-09-24 07:29:01.123456+00:00" — a space instead of "T" and
// microseconds, which not every browser's Date parser accepts.
const parseTimestamp = (value) => {
  if (!value) return null;
  const t = Date.parse(String(value).trim().replace(' ', 'T').replace(/(\.\d{3})\d+/, '$1'));
  return Number.isNaN(t) ? null : t;
};

// Displays the human-readable WORKFLOW_OPTIONS label instead of the raw
// pipeline identifier (e.g. "GenBI — Full template" instead of
// "genbi_report_template") — reads better in a narrow table cell and matches
// what the filter dropdown shows.
const workflowLabel = (pipeline) => {
  const match = WORKFLOW_OPTIONS.find((opt) => opt.value === pipeline);
  return match ? match.label : pipeline || 'Unknown';
};

const HOUR_MS = 3600 * 1000;
const DAY_MS = 24 * HOUR_MS;
// Guard against a date range that would expand into an unreadable number of
// empty buckets (e.g. "By hour" over a year).
const MAX_CHART_BUCKETS = 2000;
// "By hour" shows a recent window rather than all time: weeks of hourly
// buckets squeeze into sub-pixel slivers and make hovering one hour useless.
// "By day" stays all-time — daily density is fine across months.
const HOURLY_VIEW_HOURS = 48;

// The backend only returns buckets that had at least one run, so plotting
// them directly drew a line straight across empty hours/days and invented a
// slope. This rebuilds the full series, zero-filled. The backend's
// DATE_TRUNC runs in UTC (buckets arrive as "...T05:00:00+00:00"), so the
// grid is UTC-aligned and flooring the epoch by the step reproduces it.
const fillBuckets = (points, bucket, startIso, endIso) => {
  const step = bucket === 'hour' ? HOUR_MS : DAY_MS;
  const floorToStep = (t) => Math.floor(t / step) * step;
  // Keyed by the floored instant (not the exact one) so a bucket that isn't
  // exactly on the grid still lands in its slot instead of being dropped.
  const counts = new Map();
  const llmCounts = new Map();
  points.forEach((p) => {
    const key = floorToStep(new Date(p.bucket).getTime());
    counts.set(key, (counts.get(key) || 0) + (p.count || 0));
    llmCounts.set(key, (llmCounts.get(key) || 0) + (p.llm_calls || 0));
  });
  const known = [...counts.keys()];
  const startT = startIso
    ? floorToStep(new Date(startIso).getTime())
    : (known.length ? Math.min(...known) : null);
  if (startT === null) return [];
  const endT = floorToStep(endIso ? new Date(endIso).getTime() : Date.now());
  const out = [];
  for (let t = startT; t <= Math.max(endT, startT) && out.length < MAX_CHART_BUCKETS; t += step) {
    out.push({ start: t, end: t + step, count: counts.get(t) || 0, llmCalls: llmCounts.get(t) || 0 });
  }
  return out;
};

// ── Stat tile visuals ───────────────────────────────────────────────────
// Small, quiet shapes in each tile's own ink (currentColor, set by
// .llm-metric__visual in LlmTraces.css), so the four tints and dark theme
// carry through without per-colour props.

const TREND_DAYS = 14;

// Collapses the chart series (hourly or daily UTC buckets) into the last
// TREND_DAYS calendar days in the platform timezone, zero-filled, so the tile
// trend reads the same whatever the chart's granularity is.
const dailyTrend = (series, timeZone) => {
  const dayKey = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' });
  const byDay = new Map();
  series.forEach((b) => {
    const key = dayKey.format(b.start);
    const prev = byDay.get(key) || { count: 0, llmCalls: 0 };
    byDay.set(key, { count: prev.count + b.count, llmCalls: prev.llmCalls + b.llmCalls });
  });
  const out = [];
  for (let i = TREND_DAYS - 1; i >= 0; i -= 1) {
    const key = dayKey.format(Date.now() - i * DAY_MS);
    out.push({ day: key, ...(byDay.get(key) || { count: 0, llmCalls: 0 }) });
  }
  return out;
};

// Catmull-Rom through the points, as cubic beziers — a gentle curve instead
// of a jagged polyline. Control points are clamped to the chart box so the
// curve never dips below the zero line between two zero days.
const smoothPath = (pts, h) => {
  const clampY = (y) => Math.min(Math.max(y, 0), h);
  let d = `M ${pts[0][0]},${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i += 1) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const c1 = [p1[0] + (p2[0] - p0[0]) / 6, clampY(p1[1] + (p2[1] - p0[1]) / 6)];
    const c2 = [p2[0] - (p3[0] - p1[0]) / 6, clampY(p2[1] - (p3[1] - p1[1]) / 6)];
    d += ` C ${c1[0].toFixed(1)},${c1[1].toFixed(1)} ${c2[0].toFixed(1)},${c2[1].toFixed(1)} ${p2[0].toFixed(1)},${p2[1].toFixed(1)}`;
  }
  return d;
};

const MiniTrend = ({ points, field, label }) => {
  if (!points || points.length < 2) return null;
  const w = 100;
  const h = 32;
  const max = Math.max(...points.map((b) => b[field]), 1);
  const stepX = w / (points.length - 1);
  const pts = points.map((b, i) => [i * stepX, h - 1 - (b[field] / max) * (h - 4)]);
  const line = smoothPath(pts, h - 1);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="84" height="32" preserveAspectRatio="none" role="img" aria-label={label}>
      <title>{label}</title>
      <path d={`${line} L ${w},${h} L 0,${h} Z`} fill="currentColor" fillOpacity="0.12" />
      <path d={line} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

// Zero days get a flat stub instead of a minimum-height bar, so an empty day
// never looks like a small one.
const MiniBars = ({ points, field, label }) => {
  if (!points || points.length < 2) return null;
  const max = Math.max(...points.map((b) => b[field]), 1);
  return (
    <div className="llm-metric__bars" role="img" aria-label={label} title={label}>
      {points.map((b) => (
        <span
          key={b.day}
          style={b[field] > 0
            ? { height: `${Math.max((b[field] / max) * 100, 12)}%` }
            : { height: 2, opacity: 0.3 }}
        />
      ))}
    </div>
  );
};

// Input vs output tokens. Each segment is a partial-circle stroke offset to
// start where the previous one ended; output is the lighter shade.
const DonutRing = ({ input, output, size = 54, strokeWidth = 7 }) => {
  const total = (input || 0) + (output || 0);
  if (!total) return null;
  const r = (size - strokeWidth) / 2;
  const c = 2 * Math.PI * r;
  const inDash = ((input || 0) / total) * c;
  const inPct = Math.round(((input || 0) / total) * 100);
  const label = `${inPct}% input, ${100 - inPct}% output`;
  const ring = { cx: size / 2, cy: size / 2, r, fill: 'none', strokeWidth, transform: `rotate(-90 ${size / 2} ${size / 2})` };
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={label}>
      <title>{label}</title>
      <circle {...ring} stroke="currentColor" strokeOpacity="0.35" />
      <circle {...ring} stroke="currentColor" strokeDasharray={`${inDash} ${c - inDash}`} />
    </svg>
  );
};

// Error-rate gauge: only the track until there is an actual error.
const RadialGauge = ({ value, size = 54, strokeWidth = 7 }) => {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  const r = (size - strokeWidth) / 2;
  const c = 2 * Math.PI * r;
  const dash = (pct / 100) * c;
  const ring = { cx: size / 2, cy: size / 2, r, fill: 'none', strokeWidth, transform: `rotate(-90 ${size / 2} ${size / 2})` };
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`${pct}% of agent runs failed`}>
      <title>{`${pct}% of agent runs failed`}</title>
      <circle {...ring} stroke="currentColor" strokeOpacity="0.15" />
      {pct > 0 && <circle {...ring} stroke="currentColor" strokeLinecap="round" strokeDasharray={`${dash} ${c - dash}`} />}
    </svg>
  );
};

// ── List cells ──────────────────────────────────────────────────────────

// Status as a small coloured dot and a word, lighter than a filled pill in a
// long list. Colours live in LlmTraces.css (.llm-status--*).
// TrimContent plus the full text in a smart tooltip, only when it was cut
// short — repeating text that's already all visible is just noise. An inline
// span, so the tooltip centres on the text and not on the (wide) cell.
const TrimmedText = ({ text, maxLength }) => (
  <span className="smart-tooltip" data-tooltip={text && text.length > maxLength ? text : ''}>
    <TrimContent text={text} maxLength={maxLength} />
  </span>
);

const StatusDot = ({ status }) => (
  <span className={`llm-status llm-status--${String(status || 'unknown').toLowerCase()}`}>
    {statusLabel(status)}
  </span>
);

// Token count with a thin bar sized against the heaviest row on the page, so
// the expensive runs stand out without reading every number.
const TokenCell = ({ value, max }) => {
  const pct = max > 0 && value ? Math.max((value / max) * 100, 2) : 0;
  return (
    <div className="llm-tokens">
      <span>{formatNumber(value)}</span>
      <span className="llm-tokens__track" title="Relative to the largest on this page">
        <span className="llm-tokens__fill" style={{ width: `${pct}%` }} />
      </span>
    </div>
  );
};

// ── Agent runs by user (sunburst) ───────────────────────────────────────
// Inner ring: one arc per user, sized by agent runs. Outer ring: that user's
// runs split by template, in a lighter shade of the user's colour. Hover
// previews a user; click (on the ring or the list) pins it — the user's arcs
// scale out a little, the rest fade, and the panel below shows their numbers.

const USER_COLORS = ['#7c5cff', '#d4537e', '#3f8fd2', '#2f9e6e', '#d08a1a', '#9b59b6', '#16a2a2', '#c0567a'];
const SUN_SIZE = 190;
const SUN_C = SUN_SIZE / 2;
const SUN_INNER = [43, 67];
const SUN_OUTER = [70, 91];

const polar = (r, angle) => [SUN_C + r * Math.sin(angle), SUN_C - r * Math.cos(angle)];

// Annular sector from a0 to a1 (radians, clockwise from 12 o'clock). A full
// circle is drawn just short of 2π so the arc flags stay valid.
const arcPath = (r0, r1, a0, a1) => {
  const end = Math.min(a1, a0 + 2 * Math.PI - 0.0001);
  const large = end - a0 > Math.PI ? 1 : 0;
  const [x0, y0] = polar(r1, a0);
  const [x1, y1] = polar(r1, end);
  const [x2, y2] = polar(r0, end);
  const [x3, y3] = polar(r0, a0);
  return `M ${x0} ${y0} A ${r1} ${r1} 0 ${large} 1 ${x1} ${y1} L ${x2} ${y2} A ${r0} ${r0} 0 ${large} 0 ${x3} ${y3} Z`;
};

// Rows (one per user × template) -> users with their templates, busiest first.
const groupByUser = (rows) => {
  const users = new Map();
  rows.forEach((r) => {
    const key = r.user_id || '';
    const u = users.get(key) || {
      userId: r.user_id, runs: 0, llmCalls: 0, tokens: 0, errors: 0, lastRunAt: null, templates: []
    };
    u.runs += r.agent_runs;
    u.llmCalls += r.llm_calls;
    u.tokens += r.total_tokens;
    u.errors += r.error_count;
    if (r.last_run_at && (!u.lastRunAt || r.last_run_at > u.lastRunAt)) u.lastRunAt = r.last_run_at;
    u.templates.push({
      id: r.template_id,
      name: r.template_name || (r.template_id ? `Preset ${r.template_id}` : 'No preset'),
      runs: r.agent_runs
    });
    users.set(key, u);
  });
  return [...users.values()]
    .map((u) => ({ ...u, templates: u.templates.sort((a, b) => b.runs - a.runs) }))
    .sort((a, b) => b.runs - a.runs);
};

const UserSunburst = ({ rows, userLabel, formatDateTime }) => {
  const [pinned, setPinned] = useState(null);
  const [hovered, setHovered] = useState(null);
  const users = groupByUser(rows);
  const total = users.reduce((s, u) => s + u.runs, 0);
  if (!total) return <p className="ev-small-p-dark mb-0">No agent runs yet</p>;

  const colorOf = (i) => USER_COLORS[i % USER_COLORS.length];
  const activeKey = hovered ?? pinned;
  const active = users.find((u) => (u.userId || '') === activeKey) || null;
  const pinnedUser = users.find((u) => (u.userId || '') === pinned) || null;
  const pct = (n) => Math.round((n / total) * 100);
  const toggle = (key) => setPinned((prev) => (prev === key ? null : key));

  let angle = 0;
  const arcs = users.map((u, i) => {
    const span = (u.runs / total) * 2 * Math.PI;
    const start = angle;
    angle += span;
    let tAngle = start;
    const templates = u.templates.map((tpl) => {
      const tSpan = (tpl.runs / u.runs) * span;
      const seg = { ...tpl, a0: tAngle, a1: tAngle + tSpan };
      tAngle += tSpan;
      return seg;
    });
    return { user: u, key: u.userId || '', color: colorOf(i), a0: start, a1: start + span, templates };
  });

  return (
    <div className="llm-sun">
      <div className="llm-sun__chart">
        <svg
          viewBox={`0 0 ${SUN_SIZE} ${SUN_SIZE}`}
          width={SUN_SIZE}
          height={SUN_SIZE}
          role="img"
          aria-label={`Agent runs by user: ${users.map((u) => `${userLabel(u.userId)} ${u.runs}`).join(', ')}`}
          onMouseLeave={() => setHovered(null)}
        >
          {arcs.map((a) => {
            const isActive = activeKey === a.key;
            const dimmed = activeKey !== null && !isActive;
            return (
              <g
                key={a.key}
                className={`llm-sun__user${isActive ? ' is-active' : ''}${dimmed ? ' is-dimmed' : ''}`}
                onMouseEnter={() => setHovered(a.key)}
                onClick={() => toggle(a.key)}
              >
                <path d={arcPath(SUN_INNER[0], SUN_INNER[1], a.a0, a.a1)} fill={a.color}>
                  <title>{`${userLabel(a.user.userId)} · ${a.user.runs} run${a.user.runs === 1 ? '' : 's'} (${pct(a.user.runs)}%)`}</title>
                </path>
                {a.templates.map((tpl) => (
                  <path key={tpl.id ?? tpl.name} d={arcPath(SUN_OUTER[0], SUN_OUTER[1], tpl.a0, tpl.a1)} fill={a.color} fillOpacity="0.45">
                    <title>{`${tpl.name} · ${tpl.runs} run${tpl.runs === 1 ? '' : 's'}`}</title>
                  </path>
                ))}
              </g>
            );
          })}
        </svg>
        <div className="llm-sun__center" aria-hidden="true">
          <span className="llm-sun__center-value">{formatNumber(active ? active.runs : total)}</span>
          <span className="llm-sun__center-label">
            {active ? `${pct(active.runs)}% of runs` : `agent run${total === 1 ? '' : 's'}`}
          </span>
        </div>
      </div>

      {/* Fixed height, scrolls inside: the users list grows over time and
          the detail view is taller than a short list, and neither should
          change the height of the dashboard row. */}
      <div className="llm-sun__panel">
      {pinnedUser ? (
        <div className="llm-sun__detail">
          <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
            <span className="llm-sun__name">
              <i style={{ background: arcs.find((a) => a.key === pinned).color }} />
              {userLabel(pinnedUser.userId)}
            </span>
            <button type="button" className="btn p-0 ev-fs-11 ev-link-accent" onClick={() => setPinned(null)}>
              All users
            </button>
          </div>
          <div className="llm-sun__stats">
            <div><span>Agent runs</span><strong>{formatNumber(pinnedUser.runs)}</strong></div>
            <div><span>LLM calls</span><strong>{formatNumber(pinnedUser.llmCalls)}</strong></div>
            <div><span>Tokens</span><strong>{formatNumber(pinnedUser.tokens)}</strong></div>
            <div><span>Failed</span><strong>{formatNumber(pinnedUser.errors)}</strong></div>
          </div>
          <div className="llm-sun__templates">
            {pinnedUser.templates.map((tpl) => (
              <div key={tpl.id ?? tpl.name} title={tpl.name}>
                <span className="text-truncate">{tpl.name}</span>
                <span>{tpl.runs}</span>
              </div>
            ))}
          </div>
          {pinnedUser.lastRunAt && (
            <div className="llm-sun__last">Last run {formatDateTime(pinnedUser.lastRunAt)}</div>
          )}
        </div>
      ) : (
        <div className="llm-sun__legend">
          {arcs.map((a) => (
            <button
              key={a.key}
              type="button"
              className={activeKey === a.key ? 'is-active' : ''}
              onMouseEnter={() => setHovered(a.key)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => toggle(a.key)}
            >
              <i style={{ background: a.color }} />
              <span className="text-truncate">{userLabel(a.user.userId)}</span>
              <span className="llm-sun__legend-num">{a.user.runs}</span>
              <span className="llm-sun__legend-pct">{pct(a.user.runs)}%</span>
            </button>
          ))}
        </div>
      )}
      </div>
      {/* Always rendered (text changes) so selecting a user never changes the
          panel's height. */}
      <div className="llm-sun__hint">
        {pinnedUser ? 'Click the ring to switch user · outer ring: presets' : 'Click a user to see their numbers · outer ring: presets'}
      </div>
    </div>
  );
};

// ── Tokens by LLM call (radial bar chart) ───────────────────────────────
// One ring per LLM step, outermost first, each in its own colour (matched in
// the list beside it). Arc length is that step's tokens against the largest
// step (which spans 270°): input solid, output as a lighter continuation of
// the same colour. Every ring sits on a visible track that starts at a
// 12 o'clock axis, with its token count in the empty top-left quarter, so
// each arc reads as a value on a scale. Hover links a ring and its row;
// click opens that step in the list below.

const TR_SIZE = 220;
const TR_C = TR_SIZE / 2;
const TR_OUTER = 100;
const TR_RING = 11;     // ring thickness, until many rings need thinner ones
const TR_RING_GAP = 5;
const TR_HOLE = 52;     // radius kept clear for the total in the middle
const TR_SWEEP = 1.5 * Math.PI; // 270°
const TR_MAX_RINGS = 7;
const TR_COLORS = ['#6d4aff', '#e0567f', '#2f8fdd', '#1a9e84', '#e0951b', '#9b59d0', '#d4663a'];

// Clockwise arc from 12 o'clock, stroked (not filled) so a ring is one path.
const ringArc = (r, a0, a1) => {
  const pt = (a) => [TR_C + r * Math.sin(a), TR_C - r * Math.cos(a)];
  const [x0, y0] = pt(a0);
  const [x1, y1] = pt(a1);
  return `M ${x0} ${y0} A ${r} ${r} 0 ${a1 - a0 > Math.PI ? 1 : 0} 1 ${x1} ${y1}`;
};

const compactTokens = (n) => (n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : String(n));

const TokenRadial = ({ steps, onSelectStep }) => {
  const [active, setActive] = useState(null);

  // Too many rings stop fitting: keep the largest, in step order.
  const shown = (steps.length > TR_MAX_RINGS
    ? [...steps].sort((a, b) => b.total - a.total).slice(0, TR_MAX_RINGS).sort((a, b) => a.index - b.index)
    : steps
  ).map((s, i) => ({ ...s, color: TR_COLORS[i % TR_COLORS.length] }));
  const max = Math.max(...shown.map((s) => s.total), 1);
  const total = steps.reduce((sum, s) => sum + s.total, 0);
  const totalIn = steps.reduce((sum, s) => sum + s.input, 0);
  const totalOut = steps.reduce((sum, s) => sum + s.output, 0);
  const focus = active !== null ? shown.find((s) => s.index === active) : null;
  // Rings share the band between the hole and the outer edge; with many
  // steps they get thinner rather than crowding into the centre.
  const pitch = Math.min(TR_RING + TR_RING_GAP, (TR_OUTER - TR_HOLE) / shown.length);
  const ringWidth = Math.min(TR_RING, pitch * 0.72);
  const radius = (i) => TR_OUTER - ringWidth / 2 - i * pitch;
  const innerEdge = radius(shown.length - 1) - ringWidth / 2;
  // Ring-start labels need ~12px of pitch to stay legible; below that the
  // list beside the chart carries the numbers on its own.
  const showRingLabels = pitch >= 12;

  return (
    <div className="llm-tr">
      <div className="llm-tr__chart">
        <svg viewBox={`0 0 ${TR_SIZE} ${TR_SIZE}`} width={TR_SIZE} height={TR_SIZE} role="img"
             aria-label={`Tokens by LLM call: ${formatNumber(total)} in total`}
             onMouseLeave={() => setActive(null)}>
          {shown.map((s, i) => {
            const r = radius(i);
            const end = Math.max((s.total / max) * TR_SWEEP, 0.02);
            // Unsplit counts (no input/output recorded) draw as one segment.
            const split = s.input + s.output > 0;
            const inEnd = split ? end * (s.input / (s.input + s.output)) : end;
            const dim = active !== null && active !== s.index;
            return (
              <g key={s.index} className={`llm-tr__ring${dim ? ' is-dim' : ''}`}
                 onMouseEnter={() => setActive(s.index)} onClick={() => onSelectStep(s.index)}>
                <path d={ringArc(r, 0, TR_SWEEP)} className="llm-tr__track" strokeWidth={ringWidth} />
                {inEnd > 0.001 && (
                  <path d={ringArc(r, 0, inEnd)} stroke={s.color} strokeWidth={ringWidth} />
                )}
                {split && end - inEnd > 0.001 && (
                  <path d={ringArc(r, inEnd, end)} stroke={s.color} strokeOpacity="0.38" strokeWidth={ringWidth} />
                )}
                {showRingLabels && (
                <text x={TR_C - 6} y={TR_C - r} className="llm-tr__ring-label"
                      textAnchor="end" dominantBaseline="central">
                  {compactTokens(s.total)}
                </text>
                )}
                <title>{`${s.label}: ${formatNumber(s.total)} tokens`}</title>
              </g>
            );
          })}
          {/* the axis every ring starts from */}
          <line x1={TR_C} y1={TR_C - TR_OUTER - 2} x2={TR_C} y2={TR_C - innerEdge + 2} className="llm-tr__axis" />
        </svg>
        <div className="llm-tr__center" aria-hidden="true">
          <span className="llm-tr__center-value">{formatNumber(focus ? focus.total : total)}</span>
          <span className="llm-tr__center-label">{focus ? 'tokens in this call' : 'tokens'}</span>
        </div>
      </div>

      <div className="llm-tr__legend">
        <div className="llm-tr__key">
          <span><i className="llm-tr__swatch llm-tr__swatch--in" />Input {formatNumber(totalIn)}</span>
          <span><i className="llm-tr__swatch llm-tr__swatch--out" />Output {formatNumber(totalOut)}</span>
          <span className="llm-tr__hint">Solid: input · lighter: output</span>
        </div>
        {shown.map((s) => (
          <button
            key={s.index}
            type="button"
            className={`llm-tr__row${active === s.index ? ' is-active' : ''}`}
            onMouseEnter={() => setActive(s.index)}
            onMouseLeave={() => setActive(null)}
            onClick={() => onSelectStep(s.index)}
            title="Open this step below"
          >
            <i className="llm-tr__row-dot" style={{ background: s.color }} />
            <span className="llm-tr__row-label text-truncate">
              {s.label}{s.model && <span className="llm-tr__row-model"> · {s.model}</span>}
            </span>
            <span className="llm-tr__row-split">
              {s.input + s.output > 0 ? `${formatNumber(s.input)} in · ${formatNumber(s.output)} out` : ''}
            </span>
            <span className="llm-tr__row-total">{formatNumber(s.total)}</span>
          </button>
        ))}
        {steps.length > shown.length && (
          <div className="llm-tr__hint mt-1">Showing the {shown.length} largest of {steps.length} LLM calls</div>
        )}
      </div>
    </div>
  );
};

// ── Call volume sparkline ───────────────────────────────────────────────
// Two smoothed lines over the zero-filled series — LLM calls (accent) and
// agent runs (pink) — with one hover crosshair and a combined tooltip.
// Plain SVG, stretched to the plot box in both directions — the plot grows
// to fill the panel, so it matches the height of the Runs-by-user panel next
// to it instead of leaving a gap. Markers and the tooltip are HTML on top,
// placed in % of the box, so they stay round/unscaled at any size.

const SPARK_W = 1000;
const SPARK_H = 190;
const SPARK_PAD_TOP = 14;
const SPARK_PAD_BOTTOM = 4;
const SPARK_TICKS = 6;

const CallVolumeSparkline = ({ series, bucket, timeZone }) => {
  const [hoverIndex, setHoverIndex] = useState(null);
  const plotRef = useRef(null);
  if (!series.length) return null;

  const n = series.length;
  const floorY = SPARK_H - SPARK_PAD_BOTTOM;
  const max = Math.max(...series.map((b) => Math.max(b.llmCalls, b.count)), 1);
  const xFor = (i) => (n === 1 ? SPARK_W / 2 : (i / (n - 1)) * SPARK_W);
  const yFor = (v) => floorY - (v / max) * (floorY - SPARK_PAD_TOP);
  const llmPts = series.map((b, i) => [xFor(i), yFor(b.llmCalls)]);
  const runPts = series.map((b, i) => [xFor(i), yFor(b.count)]);
  const llmLine = n > 1 ? smoothPath(llmPts, floorY) : null;
  const runLine = n > 1 ? smoothPath(runPts, floorY) : null;
  const areaOf = (line) => `${line} L ${SPARK_W},${SPARK_H} L 0,${SPARK_H} Z`;

  const totalLlm = series.reduce((s, b) => s + b.llmCalls, 0);
  const totalRuns = series.reduce((s, b) => s + b.count, 0);

  // "24 Sep" assembled from parts — en-GB renders "Sept" in current ICU.
  const dateFmt = new Intl.DateTimeFormat('en-US', { timeZone, day: 'numeric', month: 'short' });
  const timeFmt = new Intl.DateTimeFormat('en-GB', { timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  const dateOf = (ts) => {
    const parts = dateFmt.formatToParts(ts);
    const part = (type) => (parts.find((x) => x.type === type) || {}).value;
    return `${part('day')} ${part('month')}`;
  };
  const timeOf = (ts) => timeFmt.format(ts);
  // A "day" is a UTC day on the backend, so in a non-UTC zone it spans two
  // local dates — the tooltip shows the real local window.
  const spanLabel = (b) => (bucket === 'hour'
    ? `${dateOf(b.start)}, ${timeOf(b.start)}–${timeOf(b.end)}`
    : `${dateOf(b.start)} ${timeOf(b.start)} – ${dateOf(b.end)} ${timeOf(b.end)}`);

  const tickIdx = n === 1 ? [0] : [...new Set(
    Array.from({ length: Math.min(SPARK_TICKS, n) }, (_, k) => Math.round((k * (n - 1)) / (Math.min(SPARK_TICKS, n) - 1)))
  )];
  const tickLabel = (i) => (bucket === 'hour' ? `${timeOf(series[i].start)} · ${dateOf(series[i].start)}` : dateOf(series[i].start));

  const handleMove = (e) => {
    if (!plotRef.current || n < 1) return;
    const rect = plotRef.current.getBoundingClientRect();
    const rel = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    setHoverIndex(n === 1 ? 0 : Math.round(rel * (n - 1)));
  };

  const pctX = (x) => (x / SPARK_W) * 100;
  const pctY = (y) => `${(y / SPARK_H) * 100}%`;
  const hovered = hoverIndex !== null ? series[hoverIndex] : null;
  const markIndex = hoverIndex !== null ? hoverIndex : n - 1;
  const plural = (count, word) => `${formatNumber(count)} ${word}${count === 1 ? '' : 's'}`;

  return (
    <div className="llm-spark">
      <div className="llm-spark__head">
        <div className="d-flex align-items-baseline gap-2 flex-wrap">
          <span className="llm-spark__hero">{formatNumber(totalLlm)}</span>
          <span className="llm-spark__hero-label">
            LLM calls <span className="llm-spark__sep">·</span> {plural(totalRuns, 'agent run')}
          </span>
        </div>
        <div className="llm-spark__legend">
          <span><i className="llm-spark__swatch llm-spark__swatch--llm" />LLM calls</span>
          <span><i className="llm-spark__swatch llm-spark__swatch--run" />Agent runs</span>
        </div>
      </div>

      <div
        className="llm-spark__plot"
        ref={plotRef}
        onMouseMove={handleMove}
        onMouseLeave={() => setHoverIndex(null)}
      >
        <svg
          viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
          width="100%"
          height="100%"
          preserveAspectRatio="none"
          role="img"
          aria-label={`Call volume: ${plural(totalLlm, 'LLM call')} and ${plural(totalRuns, 'agent run')}`}
        >
          <defs>
            <linearGradient id="llmSparkLlmFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" style={{ stopColor: 'var(--spark-llm)', stopOpacity: 0.22 }} />
              <stop offset="100%" style={{ stopColor: 'var(--spark-llm)', stopOpacity: 0 }} />
            </linearGradient>
            <linearGradient id="llmSparkRunFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" style={{ stopColor: 'var(--spark-run)', stopOpacity: 0.16 }} />
              <stop offset="100%" style={{ stopColor: 'var(--spark-run)', stopOpacity: 0 }} />
            </linearGradient>
          </defs>
          <line x1="0" y1={floorY} x2={SPARK_W} y2={floorY} className="llm-spark__baseline" vectorEffect="non-scaling-stroke" />
          <line x1="0" y1={yFor(max / 2)} x2={SPARK_W} y2={yFor(max / 2)} className="llm-spark__grid" vectorEffect="non-scaling-stroke" />
          {runLine && (
            <>
              <path d={areaOf(runLine)} fill="url(#llmSparkRunFill)" />
              <path d={runLine} className="llm-spark__line llm-spark__line--run" vectorEffect="non-scaling-stroke" />
            </>
          )}
          {llmLine && (
            <>
              <path d={areaOf(llmLine)} fill="url(#llmSparkLlmFill)" />
              <path d={llmLine} className="llm-spark__line llm-spark__line--llm" vectorEffect="non-scaling-stroke" />
            </>
          )}
          {hoverIndex !== null && (
            <line
              x1={xFor(hoverIndex)} y1="0" x2={xFor(hoverIndex)} y2={SPARK_H}
              className="llm-spark__crosshair" vectorEffect="non-scaling-stroke"
            />
          )}
        </svg>

        <span
          className="llm-spark__dot llm-spark__dot--run"
          style={{ left: `${pctX(runPts[markIndex][0])}%`, top: pctY(runPts[markIndex][1]), opacity: hoverIndex !== null ? 1 : 0 }}
        />
        <span
          className="llm-spark__dot llm-spark__dot--llm"
          style={{ left: `${pctX(llmPts[markIndex][0])}%`, top: pctY(llmPts[markIndex][1]) }}
        />

        {hovered && (
          <div
            className="llm-spark__tooltip"
            style={{
              left: `${Math.min(Math.max(pctX(xFor(hoverIndex)), 9), 91)}%`,
              top: pctY(Math.min(llmPts[hoverIndex][1], runPts[hoverIndex][1]))
            }}
          >
            <div className="llm-spark__tooltip-date">{spanLabel(hovered)}</div>
            <div><i className="llm-spark__swatch llm-spark__swatch--llm" /><strong>{plural(hovered.llmCalls, 'LLM call')}</strong></div>
            <div><i className="llm-spark__swatch llm-spark__swatch--run" />{plural(hovered.count, 'agent run')}</div>
          </div>
        )}
      </div>

      <div className="llm-spark__ticks">
        {tickIdx.map((i, k) => (
          <span
            key={i}
            style={{
              left: `${pctX(xFor(i))}%`,
              transform: n === 1 ? 'translateX(-50%)' : k === 0 ? 'none' : k === tickIdx.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)'
            }}
          >
            {tickLabel(i)}
          </span>
        ))}
      </div>
    </div>
  );
};

// ── Date range filter ────────────────────────────────────────────────────────
// One field for the whole range. Separate "from" / "until" pickers applied on
// their own, so setting a range loaded the list twice and showed a
// half-filtered result in between. Quick ranges apply in one click; a custom
// range is picked on one calendar (first day, then last day) with a time for
// each end, and applies once, on Apply. Escape or clicking away discards the
// pick, so a half-chosen range never reaches the list. Scrolling closes it too.
//
// Value: { preset, start, end }. A preset stays a rolling window — "Last 24
// hours" moves forward on Refresh — and becomes a timestamp only when the list
// is fetched. start / end are "YYYY-MM-DDTHH:mm" local times, the same format
// datetime-local used. The popup is portalled and fixed-positioned like
// ThemedSelect's menu, so no card or scroll container clips it.
const TIME_PRESETS = [
  { value: '1h', label: 'Last hour', ms: HOUR_MS },
  { value: '24h', label: 'Last 24 hours', ms: DAY_MS },
  { value: '7d', label: 'Last 7 days', ms: 7 * DAY_MS },
  { value: '30d', label: 'Last 30 days', ms: 30 * DAY_MS }
];
const EMPTY_TIME_RANGE = { preset: '', start: '', end: '' };

const DRP_WIDTH = 480;
const DRP_GAP = 6;
const DRP_WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
// Fixed names: en-GB's short month for September is "Sept" in current browsers.
const DRP_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad2 = (n) => String(n).padStart(2, '0');

const parseLocalDateTime = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value || '');
  return m ? new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]) : null;
};

const toLocalDateTime = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

const dayStart = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const sameDay = (a, b) => Boolean(a && b) && +dayStart(a) === +dayStart(b);

const clampInt = (text, lo, hi, fallback) => {
  const n = parseInt(text, 10);
  return Number.isNaN(n) ? fallback : Math.min(hi, Math.max(lo, n));
};

const formatDay = (d) => `${pad2(d.getDate())} ${DRP_MONTHS[d.getMonth()]} ${d.getFullYear()}`;

const formatPicked = (d) =>
  `${formatDay(d)}, ${pad2(d.getHours() % 12 || 12)}:${pad2(d.getMinutes())} ${d.getHours() < 12 ? 'AM' : 'PM'}`;

const formatClock = (d) => `${d.getHours() % 12 || 12}:${pad2(d.getMinutes())} ${d.getHours() < 12 ? 'AM' : 'PM'}`;

// No year when it's this year's, to keep the closed field short.
const formatShortDay = (d) =>
  `${d.getDate()} ${DRP_MONTHS[d.getMonth()]}${d.getFullYear() !== new Date().getFullYear() ? ` ${d.getFullYear()}` : ''}`;

// short: for the closed field, which is only as wide as the other filters —
// times are left out for whole days (12:00 AM to 11:59 PM, what a custom
// range starts as). full: everything, for the tooltip when short leaves
// something out.
const timeRangeLabels = (range) => {
  if (range.preset) {
    const label = (TIME_PRESETS.find((p) => p.value === range.preset) || {}).label || '';
    return { short: label, full: label };
  }
  const start = parseLocalDateTime(range.start);
  const end = parseLocalDateTime(range.end);
  if (!start && !end) return { short: '', full: '' };
  const wholeDays = (!start || (start.getHours() === 0 && start.getMinutes() === 0))
    && (!end || (end.getHours() === 23 && end.getMinutes() === 59));
  const short = (d) => (wholeDays ? formatShortDay(d) : `${formatShortDay(d)}, ${formatClock(d)}`);
  if (start && end) {
    return {
      short: wholeDays && sameDay(start, end) ? formatShortDay(start) : `${short(start)} – ${short(end)}`,
      full: `${formatPicked(start)} – ${formatPicked(end)}`
    };
  }
  if (start) return { short: `Since ${short(start)}`, full: `Since ${formatPicked(start)}` };
  return { short: `Until ${short(end)}`, full: `Until ${formatPicked(end)}` };
};

// start_date / end_date for the list request. datetime-local values carry no
// timezone; started_at is timestamptz, so a bare "2026-09-22T14:30" would be
// read by Postgres in the session zone, not the admin's. toISOString() sends
// the same instant, unambiguously.
const timeRangeParams = (range) => {
  const preset = TIME_PRESETS.find((p) => p.value === range.preset);
  if (preset) return { start_date: new Date(Date.now() - preset.ms).toISOString(), end_date: undefined };
  const iso = (value) => (value ? new Date(value).toISOString() : undefined);
  return { start_date: iso(range.start), end_date: iso(range.end) };
};

// A time is edited as three text parts and only becomes a Date on Apply.
const toTimeParts = (hours, minutes) => ({
  hour: pad2(hours % 12 || 12),
  minute: pad2(minutes),
  meridiem: hours < 12 ? 'AM' : 'PM'
});

const withTime = (day, parts) => {
  const d = new Date(day);
  const h12 = clampInt(parts.hour, 1, 12, 12);
  d.setHours((h12 % 12) + (parts.meridiem === 'PM' ? 12 : 0), clampInt(parts.minute, 0, 59, 0), 0, 0);
  return d;
};

// ArrowUp / ArrowDown step a time field, wrapping round at either end.
const stepTimeField = (event, text, set, lo, hi) => {
  if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
  event.preventDefault();
  const n = clampInt(text, lo, hi, lo) + (event.key === 'ArrowUp' ? 1 : -1);
  set(pad2(n > hi ? lo : n < lo ? hi : n));
};

// One end of the range: its day (picked on the calendar) and its time.
const RangeTimeRow = ({ label, dayText, parts, onChange, disabled, onEnter }) => {
  const set = (key) => (next) => onChange({ ...parts, [key]: next });
  const onKeyDown = (event, key, lo, hi) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onEnter();
      return;
    }
    stepTimeField(event, parts[key], set(key), lo, hi);
  };
  return (
    <div className={`llm-drp__time ${disabled ? 'is-disabled' : ''}`}>
      <span className="llm-drp__time-label">{label}</span>
      <span className="llm-drp__time-day">{dayText}</span>
      <div className="llm-drp__time-fields">
        <input
          className="llm-drp__time-input"
          inputMode="numeric"
          maxLength={2}
          aria-label={`${label} hour`}
          value={parts.hour}
          disabled={disabled}
          onChange={(e) => set('hour')(e.target.value.replace(/\D/g, ''))}
          onBlur={() => set('hour')(pad2(clampInt(parts.hour, 1, 12, 12)))}
          onKeyDown={(e) => onKeyDown(e, 'hour', 1, 12)}
        />
        <span className="llm-drp__colon">:</span>
        <input
          className="llm-drp__time-input"
          inputMode="numeric"
          maxLength={2}
          aria-label={`${label} minute`}
          value={parts.minute}
          disabled={disabled}
          onChange={(e) => set('minute')(e.target.value.replace(/\D/g, ''))}
          onBlur={() => set('minute')(pad2(clampInt(parts.minute, 0, 59, 0)))}
          onKeyDown={(e) => onKeyDown(e, 'minute', 0, 59)}
        />
      </div>
      <div className="llm-drp__meridiem" role="group" aria-label={`${label} AM or PM`}>
        {['AM', 'PM'].map((m) => (
          <button
            key={m}
            type="button"
            disabled={disabled}
            className={parts.meridiem === m ? 'is-active' : ''}
            aria-pressed={parts.meridiem === m}
            onClick={() => set('meridiem')(m)}
          >
            {m}
          </button>
        ))}
      </div>
    </div>
  );
};

const TimeRangeFilter = ({ value, onChange, ariaLabel, placeholder = 'Any time' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const [viewMonth, setViewMonth] = useState(() => new Date());
  const [draftStart, setDraftStart] = useState(null);
  const [draftEnd, setDraftEnd] = useState(null);
  const [hoverDay, setHoverDay] = useState(null);
  const [startTime, setStartTime] = useState(() => toTimeParts(0, 0));
  const [endTime, setEndTime] = useState(() => toTimeParts(23, 59));
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  // The document listeners below outlive a render; this keeps them calling
  // the current closeMenu.
  const closeRef = useRef(() => {});

  const labels = timeRangeLabels(value);
  const label = labels.short;
  const today = dayStart(new Date());

  const startAt = draftStart ? withTime(draftStart, startTime) : null;
  const endAt = draftEnd ? withTime(draftEnd, endTime) : null;
  const endBeforeStart = Boolean(startAt && endAt && endAt < startAt);
  const canApply = Boolean(startAt) && !endBeforeStart;

  const openMenu = () => {
    const custom = !value.preset;
    const start = custom ? parseLocalDateTime(value.start) : null;
    const end = custom ? parseLocalDateTime(value.end) : null;
    setDraftStart(start ? dayStart(start) : null);
    setDraftEnd(end ? dayStart(end) : null);
    setStartTime(start ? toTimeParts(start.getHours(), start.getMinutes()) : toTimeParts(0, 0));
    setEndTime(end ? toTimeParts(end.getHours(), end.getMinutes()) : toTimeParts(23, 59));
    setHoverDay(null);
    const anchor = start || end || today;
    setViewMonth(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
    setIsOpen(true);
  };

  const closeMenu = () => {
    setIsOpen(false);
    setMenuStyle(null);
    setHoverDay(null);
  };
  closeRef.current = closeMenu;

  const commit = (next) => {
    onChange(next);
    closeMenu();
    if (triggerRef.current) triggerRef.current.focus();
  };

  const applyCustom = () => {
    if (!canApply) return;
    commit({ preset: '', start: toLocalDateTime(startAt), end: endAt ? toLocalDateTime(endAt) : '' });
  };

  // Below the trigger, or above it when only that fits — and inside the visible
  // area when it opens: under the fixed top bar, above the viewport's bottom
  // edge, clear of both sides. (A scroll closes it; see the listeners below.)
  const positionMenu = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const menu = menuRef.current;
    // scrollHeight, not offsetHeight: the popup's natural height even while a
    // cap below is in force, so the cap can't feed back into the measurement.
    const height = menu ? menu.scrollHeight : 0;
    const width = menu ? menu.offsetWidth : DRP_WIDTH;
    const topBarBottom = Array.from(document.querySelectorAll('.fixed-top'))
      .map((el) => el.getBoundingClientRect())
      .filter((r) => r.height > 0 && r.top <= 1)
      .reduce((bottom, r) => Math.max(bottom, r.bottom), 0);
    const minTop = topBarBottom + 8;
    const maxTop = Math.max(minTop, window.innerHeight - height - 8);
    const below = rect.bottom + DRP_GAP;
    const above = rect.top - DRP_GAP - height;
    const fitsBelow = below + height <= window.innerHeight - 8;
    const top = !fitsBelow && above >= minTop ? above : below;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
    // A screen too short for the whole popup: cap it to the space under the
    // bar and let it scroll inside, rather than run off the bottom.
    const available = Math.floor(window.innerHeight - 8 - minTop);
    setMenuStyle({
      position: 'fixed',
      left: `${Math.round(left)}px`,
      top: `${Math.round(Math.min(Math.max(top, minTop), maxTop))}px`,
      ...(height > available ? { maxHeight: `${Math.max(0, available)}px`, overflowY: 'auto' } : {})
    });
  }, []);

  // Layout effect: measured and placed before the first paint, so the popup
  // never flashes at the wrong spot. Re-run when the "end before start" hint
  // comes or goes — the only thing that changes the popup's height (the
  // calendar is always six weeks), and the clamp depends on that height.
  useLayoutEffect(() => {
    if (isOpen) positionMenu();
  }, [isOpen, positionMenu, endBeforeStart]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onPointerDown = (event) => {
      const inside = (rootRef.current && rootRef.current.contains(event.target))
        || (menuRef.current && menuRef.current.contains(event.target));
      if (!inside) closeRef.current();
    };
    const onKeyDown = (event) => { if (event.key === 'Escape') closeRef.current(); };
    // A scroll closes the popup — the pick is discarded, as on a click
    // outside — instead of the popup chasing its trigger over the fixed top bar
    // and off the screen. A scroll inside the popup itself (it scrolls on a
    // very short screen) doesn't count.
    const onScroll = (event) => {
      if (menuRef.current && event.target instanceof Node && menuRef.current.contains(event.target)) return;
      closeRef.current();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', positionMenu);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', positionMenu);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [isOpen, positionMenu]);

  // Always six weeks, so the popup keeps one height from month to month.
  const firstOfMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
  const days = Array.from({ length: 42 }, (_, i) =>
    new Date(firstOfMonth.getFullYear(), firstOfMonth.getMonth(), i + 1 - firstOfMonth.getDay()));
  const shiftMonth = (delta) =>
    setViewMonth(new Date(firstOfMonth.getFullYear(), firstOfMonth.getMonth() + delta, 1));

  // First click starts the range and the second ends it (the same day twice
  // is a one-day range). A click before the start, or once both ends are
  // set, starts over from that day. Nothing is in the future to pick.
  const pickDay = (d) => {
    const day = dayStart(d);
    if (!draftStart || draftEnd || day < draftStart) {
      setDraftStart(day);
      setDraftEnd(null);
    } else {
      setDraftEnd(day);
    }
    if (d.getMonth() !== firstOfMonth.getMonth()) {
      setViewMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    }
  };

  // With only the start picked, the band previews up to the hovered day.
  const bandEnd = draftEnd || (draftStart && hoverDay && hoverDay >= draftStart ? hoverDay : null);

  const menu = (
    <div
      ref={menuRef}
      className="llm-drp__menu"
      role="dialog"
      aria-label={ariaLabel}
      style={menuStyle || { position: 'fixed', top: 0, left: 0, visibility: 'hidden' }}
    >
      <div className="llm-drp__presets">
        <p className="llm-drp__presets-title">Quick ranges</p>
        {TIME_PRESETS.map((p) => (
          <button
            key={p.value}
            type="button"
            className={`llm-drp__preset ${value.preset === p.value ? 'is-active' : ''}`}
            aria-pressed={value.preset === p.value}
            onClick={() => commit({ ...EMPTY_TIME_RANGE, preset: p.value })}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="llm-drp__body">
        <div className="llm-drp__head">
          <button type="button" className="llm-drp__nav" aria-label="Previous month" onClick={() => shiftMonth(-1)}>
            <i className="bi bi-chevron-left"></i>
          </button>
          <span className="llm-drp__month">
            {firstOfMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </span>
          <button type="button" className="llm-drp__nav" aria-label="Next month" onClick={() => shiftMonth(1)}>
            <i className="bi bi-chevron-right"></i>
          </button>
        </div>

        <div className="llm-drp__grid" onMouseLeave={() => setHoverDay(null)}>
          {DRP_WEEKDAYS.map((dow) => (
            <span key={dow} className="llm-drp__dow">{dow}</span>
          ))}
          {days.map((d) => {
            const isStart = sameDay(d, draftStart);
            const isEnd = sameDay(d, bandEnd);
            const classes = [
              'llm-drp__day',
              d.getMonth() !== firstOfMonth.getMonth() && 'is-outside',
              sameDay(d, today) && 'is-today',
              draftStart && bandEnd && +d > +draftStart && +d < +bandEnd && 'is-in-range',
              isStart && 'is-range-start',
              isEnd && 'is-range-end',
              isEnd && !draftEnd && 'is-preview'
            ].filter(Boolean).join(' ');
            return (
              <button
                key={+d}
                type="button"
                className={classes}
                disabled={d > today}
                aria-pressed={isStart || (isEnd && Boolean(draftEnd))}
                aria-label={d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                onMouseEnter={() => setHoverDay(dayStart(d))}
                onClick={() => pickDay(d)}
              >
                {d.getDate()}
              </button>
            );
          })}
        </div>
        <p className="llm-drp__pick-hint">
          {!draftStart ? 'Pick the first day, then the last.' : !draftEnd ? 'Now pick the last day — or Apply for “until now”.' : ' '}
        </p>

        <div className="llm-drp__times">
          <RangeTimeRow
            label="From"
            dayText={draftStart ? formatDay(draftStart) : '—'}
            parts={startTime}
            onChange={setStartTime}
            disabled={!draftStart}
            onEnter={applyCustom}
          />
          <RangeTimeRow
            label="To"
            dayText={draftEnd ? formatDay(draftEnd) : 'Now'}
            parts={endTime}
            onChange={setEndTime}
            disabled={!draftEnd}
            onEnter={applyCustom}
          />
        </div>
        {endBeforeStart && <p className="llm-drp__hint">The end has to be after the start.</p>}

        <div className="llm-drp__foot">
          <button type="button" className="llm-drp__link" onClick={() => commit(EMPTY_TIME_RANGE)}>
            Clear
          </button>
          <button type="button" className="llm-drp__link ms-auto" onClick={closeMenu}>
            Cancel
          </button>
          <button type="button" className="llm-drp__apply" disabled={!canApply} onClick={applyCustom}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div ref={rootRef} className="llm-drp">
      <button
        ref={triggerRef}
        type="button"
        className={`themed-select__trigger llm-drp__trigger smart-tooltip ${label ? 'has-value' : ''}`}
        data-tooltip={labels.full !== labels.short ? labels.full : ''}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        onClick={() => (isOpen ? closeMenu() : openMenu())}
      >
        <i className="bi bi-calendar3 llm-drp__icon" aria-hidden="true"></i>
        <span className={label ? '' : 'themed-select__placeholder'}>{label || placeholder}</span>
        {!label && (
          <i className={`bi bi-chevron-down themed-select__caret ${isOpen ? 'is-open' : ''}`} aria-hidden="true"></i>
        )}
      </button>
      {label && (
        <button
          type="button"
          className="llm-drp__clear"
          aria-label="Clear the time range"
          onClick={() => { if (isOpen) closeMenu(); onChange(EMPTY_TIME_RANGE); }}
        >
          <i className="bi bi-x-lg"></i>
        </button>
      )}
      {isOpen && ReactDOM.createPortal(menu, document.body)}
    </div>
  );
};

const LlmTraces = () => {
  const navigate = useNavigate();
  const accessToken = GetCookie('access_token');
  const { formatDateTime, timezone } = useTimezone();

  // Table + pagination
  const [traces, setTraces] = useState([]);
  const [total, setTotal] = useState(0);
  // "runs" = one row per agent run; "sessions" = one row per execution
  // (session_id), from the /sessions endpoint. sessionFilter narrows the
  // runs view to a single execution.
  const [listView, setListView] = useState('runs');
  const [sessions, setSessions] = useState([]);
  const [sessionFilter, setSessionFilter] = useState('');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // User and Workflow are both dropdowns now — User sourced from the
  // existing iam-users/list endpoint (see the effect below), Workflow from
  // the fixed WORKFLOW_OPTIONS enum — so neither needs the free-text/
  // committed pattern; both select immediately like Status.
  const [userFilter, setUserFilter] = useState('');
  const [userOptions, setUserOptions] = useState([{ value: '', label: 'All users' }]);
  const [pipelineFilter, setPipelineFilter] = useState('');

  // Template / Agent dropdowns, from /llm-traces/filter-options (every
  // template; agents once they have runs). Values are kept as strings for
  // ThemedSelect and sent as numbers. Each agent carries its template_id so
  // the Agent list narrows to the chosen template.
  const [templateFilter, setTemplateFilter] = useState('');
  const [agentFilter, setAgentFilter] = useState('');
  const [filterOptions, setFilterOptions] = useState({ templates: [], agents: [] });
  const [filterOptionsTick, setFilterOptionsTick] = useState(0);

  const [statusFilter, setStatusFilter] = useState('');
  // Date range — a quick range or a custom from/to, to the minute; see
  // TimeRangeFilter for the shape.
  const [timeRange, setTimeRange] = useState(EMPTY_TIME_RANGE);

  // One window per range pick / Refresh. A quick range ("Last 24 hours") is
  // relative to now, so working it out on every render gave a new timestamp
  // each time — and with that timestamp as an effect dependency, the tiles,
  // chart and sunburst refetched in a loop. Held here, every request and label
  // between two Refreshes uses the same window, and paging doesn't shift it.
  const timeRangeBounds = useMemo(
    () => timeRangeParams(timeRange),
    // refreshTrigger: Refresh is what moves a quick range forward.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [timeRange, refreshTrigger]
  );
  const dateRange = () => timeRangeBounds;
  const { start_date: startDateTime, end_date: endDateTime } = timeRangeBounds;

  // Collapsed by default: the filter panel was taking up a lot of vertical
  // space before any filter was even touched.
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Dashboard tiles + chart carry their own loading/error state (matching
  // AdminDashboard's MetricCard pattern) instead of the page-wide
  // ProcessingPopup, so a failed summary/chart call never blocks the table
  // from rendering.
  const [summary, setSummary] = useState({
    total_calls: null, total_llm_calls: null, total_tokens: null,
    input_tokens: null, output_tokens: null, error_rate: null
  });
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState('');

  // Raw buckets plus the granularity they were fetched with, kept together so
  // a granularity switch never fills old buckets with the new step size.
  const [chartResult, setChartResult] = useState(null);
  const [chartLoading, setChartLoading] = useState(true);
  const [chartError, setChartError] = useState('');
  const [chartGranularity, setChartGranularity] = useState('day');

  // Agent runs per user (and template inside each user), for the sunburst.
  const [userBreakdown, setUserBreakdown] = useState([]);
  const [userBreakdownLoading, setUserBreakdownLoading] = useState(true);
  const [userBreakdownError, setUserBreakdownError] = useState('');

  // Detail modal
  const [detailTrace, setDetailTrace] = useState(null);
  const [detailChildren, setDetailChildren] = useState([]);
  const [detailSteps, setDetailSteps] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState('');

  const API_BASE_URL = '/pceapi/administration';
  const headers = { Authorization: accessToken };


  // Every filter on the page narrows the tiles, chart and sunburst the same
  // way it narrows the table — template/agent/user, date range, status,
  // workflow, and a session drill-down. Only page/size (pagination) are
  // list-only. (No free-text search: agent and preset names are the Agent and
  // Preset filters, and an ID-only box was rarely useful — it was removed.)
  const buildDashboardFilters = () => ({
    template_id: templateFilter ? Number(templateFilter) : undefined,
    agent_id: agentFilter ? Number(agentFilter) : undefined,
    user_id: userFilter || undefined,
    status: statusFilter || undefined,
    pipeline: pipelineFilter || undefined,
    session_id: sessionFilter || undefined,
    ...dateRange()
  });

  const buildListFilters = () => ({
    template_id: templateFilter ? Number(templateFilter) : undefined,
    agent_id: agentFilter ? Number(agentFilter) : undefined,
    user_id: userFilter || undefined,
    status: statusFilter || undefined,
    pipeline: pipelineFilter || undefined,
    // The same window the tiles and chart use (see timeRangeBounds).
    ...dateRange()
  });

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listView, sessionFilter, page, size, templateFilter, agentFilter, userFilter, statusFilter, pipelineFilter, timeRange, refreshTrigger]);

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger, templateFilter, agentFilter, userFilter, statusFilter, pipelineFilter, sessionFilter, timeRange]);

  // Own effect: the chart also reacts to chartGranularity, which the summary
  // tiles don't care about, so it isn't bundled into the effect above.
  useEffect(() => {
    fetchChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger, chartGranularity, templateFilter, agentFilter, userFilter, statusFilter, pipelineFilter, sessionFilter, timeRange]);

  useEffect(() => {
    fetchUserBreakdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger, templateFilter, agentFilter, userFilter, statusFilter, pipelineFilter, sessionFilter, timeRange]);

  // User dropdown options: reuses the existing, already-implemented
  // iam-users/list endpoint (same one AdminDashboard's directory uses)
  // instead of waiting on a new LLM-traces-specific endpoint. One-time
  // fetch on mount — the set of platform users doesn't change mid-session
  // often enough to warrant refetching on every filter/refresh.
  useEffect(() => {
    let current = true;
    axios
      .post(`${API_BASE_URL}/iam-users/list`, { page: 1, size: 200 }, { headers })
      .then((response) => {
        if (!current) return;
        const data = response.data;
        if (data.status === 'NotAuthorized') {
          navigate('/admin-login');
          return;
        }
        if (data.status === 'failed') return;
        const rows = data.data || [];
        setUserOptions([
          { value: '', label: 'All users' },
          // Email as the second line, so the search finds a user by it too.
          ...rows.map((u) => ({
            value: u.user_id || u.id,
            label: u.name || u.email || String(u.user_id || u.id),
            hint: u.name && u.email ? u.email : undefined
          }))
        ]);
      })
      .catch((error) => {
        // Non-blocking: the User filter just stays at "All users" only if
        // this fails — it must never break the rest of the page.
        console.error('Error fetching user options:', error);
      });
    return () => {
      current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Coming back to this tab refetches the Template / Agent options, so a
  // template made in the IAM console meanwhile is there without a reload.
  useEffect(() => {
    const onFocus = () => setFilterOptionsTick((n) => n + 1);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  // Also refetched on Refresh, for templates made and agents first run
  // while this tab stayed open.
  useEffect(() => {
    let current = true;
    axios
      .post(`${API_BASE_URL}/llm-traces/filter-options`, {}, { headers })
      .then((response) => {
        if (!current) return;
        const data = response.data;
        if (data.status === 'NotAuthorized') {
          navigate('/admin-login');
          return;
        }
        if (data.status === 'failed') return;
        setFilterOptions({
          templates: data.data?.templates || [],
          agents: data.data?.agents || []
        });
      })
      .catch((error) => {
        // Non-blocking, same as the user options: the dropdowns just stay
        // at "All".
        console.error('Error fetching LLM trace filter options:', error);
      });
    return () => {
      current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger, filterOptionsTick]);

  const fetchList = async () => {
    setIsProcessingPopupVisible(true);
    const isSessions = listView === 'sessions';
    try {
      const response = await axios.post(
        `${API_BASE_URL}/llm-traces/${isSessions ? 'sessions' : 'list'}`,
        isSessions
          ? { page, size, ...buildListFilters() }
          : { page, size, ...buildListFilters(), session_id: sessionFilter || undefined },
        { headers }
      );
      const data = response.data;

      if (data.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        navigate('/admin-login');
        return;
      }
      if (data.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(data.message || 'Failed to load LLM traces');
        setIsErrorPopupVisible(true);
        return;
      }

      if (isSessions) setSessions(data.data || []);
      else setTraces(data.data || []);
      setTotal(data.total || 0);
      setDetailTrace(null);
    } catch (error) {
      console.error('Error fetching LLM traces:', error);
      setPopupErrorMessage(error.message || 'Failed to load LLM traces');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  const fetchSummary = async () => {
    setSummaryLoading(true);
    setSummaryError('');
    try {
      const response = await axios.post(`${API_BASE_URL}/llm-traces/dashboard/summary`, buildDashboardFilters(), { headers });
      const data = response.data;

      if (data.status === 'NotAuthorized') {
        navigate('/admin-login');
        return;
      }
      if (data.status === 'failed') {
        setSummaryError(data.message || 'Failed to load summary');
        return;
      }

      setSummary(data.data || {});
    } catch (error) {
      console.error('Error fetching LLM trace summary:', error);
      setSummaryError(error.message || 'Failed to load summary');
    } finally {
      setSummaryLoading(false);
    }
  };

  const fetchChart = async () => {
    setChartLoading(true);
    setChartError('');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/llm-traces/dashboard/calls-over-time`,
        { bucket: chartGranularity, ...buildDashboardFilters() },
        { headers }
      );
      const data = response.data;

      if (data.status === 'NotAuthorized') {
        navigate('/admin-login');
        return;
      }
      if (data.status === 'failed') {
        setChartError(data.message || 'Failed to load calls-over-time chart');
        setChartResult(null);
        return;
      }

      setChartResult({ points: data.data || [], bucket: chartGranularity });
    } catch (error) {
      console.error('Error fetching calls-over-time chart:', error);
      setChartError(error.message || 'Failed to load calls-over-time chart');
      setChartResult(null);
    } finally {
      setChartLoading(false);
    }
  };

  const fetchUserBreakdown = async () => {
    setUserBreakdownLoading(true);
    setUserBreakdownError('');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/llm-traces/dashboard/user-breakdown`,
        buildDashboardFilters(),
        { headers }
      );
      const data = response.data;

      if (data.status === 'NotAuthorized') {
        navigate('/admin-login');
        return;
      }
      if (data.status === 'failed') {
        setUserBreakdownError(data.message || 'Failed to load user breakdown');
        return;
      }
      setUserBreakdown(data.data || []);
    } catch (error) {
      console.error('Error fetching LLM trace user breakdown:', error);
      setUserBreakdownError(error.message || 'Failed to load user breakdown');
    } finally {
      setUserBreakdownLoading(false);
    }
  };

  const fetchTraceDetail = async (runId) => {
    setDetailLoading(true);
    setDetailError('');
    try {
      const response = await axios.post(`${API_BASE_URL}/llm-traces/detail`, { run_id: runId }, { headers });
      const data = response.data;

      if (data.status === 'NotAuthorized') {
        navigate('/admin-login');
        return;
      }
      if (data.status === 'failed') {
        setDetailError(data.message || 'Failed to load trace detail');
        return;
      }

      // data is the llm_run row itself, plus child_calls (the lightweight step
      // index from agent_execution_steps — always there) and execution_log
      // (the full S3 step log with prompts/outputs — null if step logging was
      // off for that run or the S3 read failed). Merged over the list row so
      // a partial response still leaves the list fields on screen.
      const detail = data.data || {};
      setDetailTrace((prev) => ({ ...prev, ...detail }));
      setDetailChildren(detail.child_calls || []);
      setDetailSteps((detail.execution_log && detail.execution_log.steps) || []);
    } catch (error) {
      console.error('Error fetching trace detail:', error);
      setDetailError(error.message || 'Failed to load trace detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const openDetail = (row) => {
    setDetailTrace(row);
    setDetailChildren([]);
    setDetailSteps([]);
    setDetailError('');
    fetchTraceDetail(row.id);
  };

  const closeDetail = () => {
    setDetailTrace(null);
  };

  // Jump from a session (row or trace detail) to the runs inside it.
  const showSessionRuns = (sessionId) => {
    setDetailTrace(null);
    setSessionFilter(sessionId);
    setListView('runs');
    setPage(1);
  };

  const switchListView = (view) => {
    if (view === listView) return;
    setListView(view);
    setSessionFilter('');
    setPage(1);
  };

  // llm_run carries only user_id; the name comes from the iam-users list the
  // User filter already loaded. Falls back to the raw id if that list failed
  // or the user has since been deleted.
  const userLabel = (userId) => {
    if (!userId) return 'Unknown user';
    const match = userOptions.find((opt) => opt.value === userId);
    return match ? match.label : userId;
  };

  // Detail modal: prefer the full S3 step log; fall back to the Postgres
  // step index (no content, no tool_name/model) when the log is missing.
  const hasFullLog = detailSteps.length > 0;
  const runMeta = (detailTrace && detailTrace.run_metadata) || {};
  const shownSteps = hasFullLog ? detailSteps : detailChildren;
  const detailModels = [...new Set(shownSteps.map((s) => s.model_id).filter(Boolean))];
  const llmCallCount = shownSteps.filter(isLlmStep).length;
  // Per-call tokens only exist in the full S3 log; the Postgres step index
  // has none. Steps that recorded no tokens are left out of the chart.
  const tokenSteps = hasFullLog
    ? shownSteps
        .map((s, i) => ({
          index: i,
          label: stepDisplay(s).label,
          model: shortModelId(s.model_id),
          input: s.input_tokens || 0,
          output: s.output_tokens || 0,
          total: s.total_tokens || (s.input_tokens || 0) + (s.output_tokens || 0)
        }))
        .filter((row, i) => isLlmStep(shownSteps[i]) && row.total > 0)
    : [];
  // Opens a step's row in the list and brings it into view.
  const openStep = (index) => {
    const el = document.getElementById(`llm-step-${index}`);
    if (!el) return;
    el.open = true;
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };
  const toolCallCount = shownSteps.filter((s) => s.step_type === 'tool_call').length;

  // Timeline: each step's bar is placed by its start offset within the run
  // and sized by its own latency, both as a share of the run's duration. If
  // a timestamp can't be read, the step is laid end-to-end after the previous
  // one instead so the bar still shows relative duration.
  const runStartT = detailTrace ? parseTimestamp(detailTrace.started_at) : null;
  const runTotalMs = Math.max(
    (detailTrace && detailTrace.latency_ms) || 0,
    shownSteps.reduce((sum, s) => sum + (s.latency_ms || 0), 0),
    1
  );
  let cursorMs = 0;
  const stepBars = shownSteps.map((s) => {
    const stepStartT = parseTimestamp(s.started_at);
    const offsetMs = runStartT !== null && stepStartT !== null ? Math.max(stepStartT - runStartT, 0) : cursorMs;
    cursorMs = offsetMs + (s.latency_ms || 0);
    const widthPct = Math.max(((s.latency_ms || 0) / runTotalMs) * 100, 1);
    const leftPct = Math.min((offsetMs / runTotalMs) * 100, 100 - widthPct);
    return { leftPct, widthPct, offsetMs };
  });

  // The chart follows the selected date range on the backend now; when no
  // range is set it's still all-time. By day, "all time" shows first bucket
  // -> now; by hour it shows only the last HOURLY_VIEW_HOURS regardless, so
  // hourly buckets never squeeze into unreadable slivers.
  const hourlyView = chartResult && chartResult.bucket === 'hour';
  const chartRangeStart = startDateTime
    ? new Date(startDateTime).toISOString()
    : (hourlyView ? new Date(Date.now() - HOURLY_VIEW_HOURS * HOUR_MS).toISOString() : undefined);
  const chartRangeEnd = endDateTime ? new Date(endDateTime).toISOString() : undefined;
  const chartSeries = chartResult
    ? fillBuckets(chartResult.points, chartResult.bucket, chartRangeStart, chartRangeEnd)
    : [];
  const chartHasActivity = chartSeries.some((b) => b.count > 0 || b.llmCalls > 0);
  // Tile trends: last TREND_DAYS local days, built from their own window
  // (not the chart's) so the hourly view's 48h cap never empties them.
  // Skipped while the chart is loading or failed.
  const tileSeries = chartResult && !chartLoading && !chartError
    ? fillBuckets(chartResult.points, chartResult.bucket, new Date(Date.now() - (TREND_DAYS + 1) * DAY_MS).toISOString(), undefined)
    : [];
  const tileTrend = tileSeries.length ? dailyTrend(tileSeries, timezone) : null;
  const tileTrendLabel = `Last ${TREND_DAYS} days`;
  // Token bars in the lists are relative to the heaviest row on the page.
  const maxRunTokens = traces.reduce((m, r) => Math.max(m, r.total_tokens || 0), 0);
  const maxSessionTokens = sessions.reduce((m, r) => Math.max(m, r.total_tokens || 0), 0);

  const templateOptions = [
    { value: '', label: 'All presets' },
    ...filterOptions.templates.map((tpl) => ({
      value: String(tpl.template_id),
      label: tpl.template_name || `Preset ${tpl.template_id}`
    }))
  ];
  const agentOptions = [
    { value: '', label: templateFilter ? 'All agents in this preset' : 'All agents' },
    ...filterOptions.agents
      .filter((ag) => !templateFilter || String(ag.template_id) === templateFilter)
      .map((ag) => ({
        value: String(ag.agent_id),
        label: ag.agent_name || `Agent ${ag.agent_id}`
      }))
  ];
  const optionLabel = (options, value) => (options.find((opt) => opt.value === value) || {}).label;

  const changeTemplateFilter = (next) => {
    setTemplateFilter(next);
    // Drop an agent that doesn't belong to the newly chosen template.
    const agent = filterOptions.agents.find((ag) => String(ag.agent_id) === agentFilter);
    if (next && agent && String(agent.template_id) !== next) setAgentFilter('');
    setPage(1);
  };

  // The tiles and chart now follow every filter the list does.
  const dashboardFilterNames = [
    templateFilter && optionLabel(templateOptions, templateFilter),
    agentFilter && optionLabel(agentOptions, agentFilter),
    userFilter && userLabel(userFilter),
    statusFilter && optionLabel(STATUS_OPTIONS, statusFilter),
    pipelineFilter && workflowLabel(pipelineFilter),
    sessionFilter && `Session ${sessionFilter.slice(0, 8)}`
  ].filter(Boolean);
  const dashboardDateLabel = startDateTime || endDateTime
    ? [
        startDateTime ? formatDateTime(new Date(startDateTime).toISOString()) : 'Earliest',
        endDateTime ? formatDateTime(new Date(endDateTime).toISOString()) : 'Now'
      ].join(' – ')
    : 'All time';
  // Tiles are narrow, so they only say whether a filter is on; the chart
  // subtitle has room for the names.
  const dashboardScopeLabel = dashboardFilterNames.length || startDateTime || endDateTime
    ? `${dashboardDateLabel} · Filtered`
    : dashboardDateLabel;
  const chartScopeLabel = [
    chartGranularity === 'hour' && !startDateTime && !endDateTime ? `Last ${HOURLY_VIEW_HOURS} hours` : dashboardDateLabel,
    ...dashboardFilterNames
  ].join(' · ');

  // So the count next to "Filters" is accurate even while the panel is
  // collapsed and these fields are out of view.
  const activeFilterCount = [templateFilter, agentFilter, userFilter, statusFilter, pipelineFilter,
    timeRange.preset || timeRange.start || timeRange.end]
    .filter(Boolean).length;

  // Every panel filter back to its default in one go. The updates batch into
  // one render, so the list, tiles and chart each refetch once, not per field.
  // A session drill-down has its own clear control and is left alone.
  const clearFilters = () => {
    setTemplateFilter('');
    setAgentFilter('');
    setUserFilter('');
    setStatusFilter('');
    setPipelineFilter('');
    setTimeRange(EMPTY_TIME_RANGE);
    setPage(1);
  };

  return (
    <div className="main-window">
      <AdminTopNav />
      <div className="container-fluid">
        <div className="row">
          <AdminSideNav />
          <main className="col-12 col-md-9 col-lg-10 ms-sm-auto console-main-window">
            <div className="mx-5 pb-5">
              <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center mb-0 margin-top-4">
                <p className="ev-med-p-blue mb-1">
                  LLM Traces <span className="ev-light-reg">({total})</span>
                </p>
              </div>
              <p className="ev-message-card2 mb-4">
                Agent runs and LLM calls captured during agent pipeline execution.
              </p>

              {/* Everything from the filter panel through the table shares one
                  .admin-overview wrapper, reusing AdminDashboard's own panel
                  system (.ad-panel/.ad-metric — subtle shadow, consistent
                  card chrome) instead of the heavier generic ev-card-4 look.
                  That CSS is scoped under .admin-overview and already loaded
                  globally via App.js; padding/width are reset inline since
                  the class also carries full-page layout rules meant for
                  AdminDashboard itself. Filters sit above the totals/chart/
                  table they scope, rather than buried in the table's own
                  toolbar, so it reads clearly that they drive everything
                  below (not just the table). */}
              <div className="admin-overview llm-traces" style={{ padding: 0, margin: 0, maxWidth: 'none' }}>
              <section className="ad-panel mb-4">
                <div className="ad-panel__heading">
                  <div className="d-flex align-items-center gap-3">
                    <span className="ad-panel__heading-icon"><i className="bi bi-funnel" style={{ fontSize: '14px' }}></i></span>
                    <div>
                      <h2>
                        Filters
                        {activeFilterCount > 0 && (
                          <span className="ev-message-card1 ev-fs-11 ms-2">{activeFilterCount} active</span>
                        )}
                      </h2>
                      <p>Preset, Agent and User also narrow the totals and chart; the other filters apply to the list only.</p>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {activeFilterCount > 0 && (
                      <button
                        type="button"
                        className="ev-btn-blue-outline-round ev-bold-reg"
                        onClick={clearFilters}
                      >
                        <i className="bi bi-x-circle"></i> Clear filters
                      </button>
                    )}
                    <button
                      className="ev-btn-lightblue-fill-round ev-bold-reg"
                      onClick={() => setRefreshTrigger((prev) => prev + 1)}
                    >
                      <i className="bi bi-arrow-repeat"></i> Refresh
                    </button>
                    <button
                      type="button"
                      className="ev-btn-light-fill ev-radius-xsmall smart-tooltip"
                      data-tooltip={filtersExpanded ? 'Collapse filters' : 'Expand filters'}
                      onClick={() => setFiltersExpanded((prev) => !prev)}
                    >
                      <i className={`bi ${filtersExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                    </button>
                  </div>
                </div>

                {filtersExpanded && (
                <div className="row g-3" style={{ padding: '0 20px 20px' }}>
                  <div className="col-12 col-md-6 col-lg-3">
                    <label className="ev-reg-p-dark mb-1 d-block">Preset</label>
                    <div className="themed-select--compact">
                      <ThemedSelect
                        value={templateFilter}
                        options={templateOptions}
                        ariaLabel="Filter by preset"
                        enableSearch
                        searchPlaceholder="Search presets"
                        onChange={changeTemplateFilter}
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-6 col-lg-3">
                    <label className="ev-reg-p-dark mb-1 d-block">Agent</label>
                    <div className="themed-select--compact">
                      <ThemedSelect
                        value={agentFilter}
                        options={agentOptions}
                        ariaLabel="Filter by agent"
                        enableSearch
                        searchPlaceholder="Search agents"
                        onChange={(next) => { setAgentFilter(next); setPage(1); }}
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-6 col-lg-3">
                    <label className="ev-reg-p-dark mb-1 d-block">User</label>
                    <div className="themed-select--compact">
                      <ThemedSelect
                        value={userFilter}
                        options={userOptions}
                        ariaLabel="Filter by user"
                        enableSearch
                        searchPlaceholder="Search users"
                        onChange={(next) => { setUserFilter(next); setPage(1); }}
                      />
                    </div>
                  </div>
                  {/* "Workflow" in the UI, "pipeline" on the data model
                      (llm_run.pipeline) — the process that produced the
                      trace, e.g. "GenBI Agent" or "GenBI Doc Import" —
                      distinct from agent_name (a specific agent within that
                      workflow) and run_type (agent run vs. individual LLM
                      call). A fixed dropdown, not free text: Phase 1's
                      instrumented pool managers are a closed, known set. */}
                  <div className="col-12 col-md-6 col-lg-3">
                    <label className="ev-reg-p-dark mb-1 d-block">Workflow</label>
                    <div className="themed-select--compact">
                      <ThemedSelect
                        value={pipelineFilter}
                        options={WORKFLOW_OPTIONS}
                        ariaLabel="Filter by workflow"
                        enableSearch
                        searchPlaceholder="Search workflows"
                        onChange={(next) => { setPipelineFilter(next); setPage(1); }}
                      />
                    </div>
                  </div>

                  {/* Second row: the list-only filters, on the same 4-column
                      grid as the dropdowns above. */}
                  <div className="col-12 col-md-6 col-lg-3">
                    <label className="ev-reg-p-dark mb-1 d-block">Status</label>
                    <div className="themed-select--compact">
                      <ThemedSelect
                        value={statusFilter}
                        options={STATUS_OPTIONS}
                        ariaLabel="Filter by status"
                        enableSearch
                        searchPlaceholder="Search statuses"
                        onChange={(next) => { setStatusFilter(next); setPage(1); }}
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-6 col-lg-3">
                    <label className="ev-reg-p-dark mb-1 d-block">Date range</label>
                    <div className="themed-select--compact">
                      <TimeRangeFilter
                        value={timeRange}
                        ariaLabel="Filter by date range"
                        onChange={(next) => { setTimeRange(next); setPage(1); }}
                      />
                    </div>
                  </div>
                </div>
                )}
              </section>

              {/* .llm-metrics (LlmTraces.css) sets the 4-column grid and a
                  tint per tile on top of AdminDashboard's .ad-metric card.
                  total_calls counts AGENT_RUN rows (GenBI agent executions);
                  total_llm_calls counts the model-invocation steps inside them. */}
              <section className="ad-metrics llm-metrics mb-4" aria-label="LLM trace totals">
                  <article className="ad-metric llm-metric llm-metric--blue">
                    <div className="ad-metric__top">
                      <span className="ad-metric__label">Agent Runs</span>
                      <span className="ad-metric__icon"><i className="bi bi-diagram-3"></i></span>
                    </div>
                    <div className="llm-metric__body">
                      <div className="ad-metric__value" aria-busy={summaryLoading}>
                        {summaryLoading || summaryError ? '—' : formatNumber(summary.total_calls)}
                      </div>
                      {tileTrend && (
                        <div className="llm-metric__visual">
                          <MiniTrend points={tileTrend} field="count" label={`Agent runs, ${tileTrendLabel.toLowerCase()}`} />
                          <span className="llm-metric__caption">{tileTrendLabel}</span>
                        </div>
                      )}
                    </div>
                    <p className="ad-muted ad-metric__description">
                      {summaryError ? 'Currently unavailable' : dashboardScopeLabel}
                    </p>
                  </article>
                  <article className="ad-metric llm-metric llm-metric--purple">
                    <div className="ad-metric__top">
                      <span className="ad-metric__label">LLM Calls</span>
                      <span className="ad-metric__icon"><i className="bi bi-graph-up-arrow"></i></span>
                    </div>
                    <div className="llm-metric__body">
                      <div className="ad-metric__value" aria-busy={summaryLoading}>
                        {summaryLoading || summaryError ? '—' : formatNumber(summary.total_llm_calls)}
                      </div>
                      {tileTrend && (
                        <div className="llm-metric__visual">
                          <MiniBars points={tileTrend} field="llmCalls" label={`LLM calls, ${tileTrendLabel.toLowerCase()}`} />
                          <span className="llm-metric__caption">{tileTrendLabel}</span>
                        </div>
                      )}
                    </div>
                    <p className="ad-muted ad-metric__description">
                      {summaryError ? 'Currently unavailable' : dashboardScopeLabel}
                    </p>
                  </article>
                  <article className="ad-metric llm-metric llm-metric--green">
                    <div className="ad-metric__top">
                      <span className="ad-metric__label">Total Tokens</span>
                    </div>
                    <div className="llm-metric__body">
                      <div className="ad-metric__value" aria-busy={summaryLoading}>
                        {summaryLoading || summaryError ? '—' : formatNumber(summary.total_tokens)}
                      </div>
                      {!summaryLoading && !summaryError && (
                        <div className="llm-metric__visual llm-metric__visual--ring">
                          <DonutRing input={summary.input_tokens} output={summary.output_tokens} />
                        </div>
                      )}
                    </div>
                    <p className="ad-muted ad-metric__description">
                      {summaryError ? 'Currently unavailable' : (
                        <span className="llm-metric__split">
                          <strong>{formatNumber(summary.input_tokens)}</strong> in
                          <span className="llm-metric__sep">·</span>
                          <strong>{formatNumber(summary.output_tokens)}</strong> out
                          <span className="llm-metric__sep">·</span>
                          {dashboardScopeLabel}
                        </span>
                      )}
                    </p>
                  </article>
                  <article className="ad-metric llm-metric llm-metric--pink">
                    <div className="ad-metric__top">
                      <span className="ad-metric__label">Error Rate</span>
                    </div>
                    <div className="llm-metric__body">
                      <div className="ad-metric__value" aria-busy={summaryLoading}>
                        {summaryLoading || summaryError ? '—' : formatErrorRate(summary.error_rate)}
                      </div>
                      {!summaryLoading && !summaryError && (
                        <div className="llm-metric__visual llm-metric__visual--ring">
                          <RadialGauge value={summary.error_rate} />
                        </div>
                      )}
                    </div>
                    <p className="ad-muted ad-metric__description">
                      {summaryError ? 'Currently unavailable' : dashboardScopeLabel}
                    </p>
                  </article>
                </section>

              {/* Call volume (wide) and runs-by-user (narrow) side by side;
                  they stack below 1200px (LlmTraces.css). */}
              <div className="llm-dash-row mb-4">
              <section className="ad-panel">
                <div className="ad-panel__heading">
                  <div className="d-flex align-items-center gap-3">
                    <span className="ad-panel__heading-icon"><i className="bi bi-graph-up" style={{ fontSize: '14px' }}></i></span>
                    <div>
                      <h2>Call volume</h2>
                      <p>{chartScopeLabel}</p>
                    </div>
                  </div>
                  <div className="themed-select--compact" style={{ width: '130px' }}>
                    <ThemedSelect
                      value={chartGranularity}
                      options={GRANULARITY_OPTIONS}
                      ariaLabel="Chart time bucket"
                      onChange={(next) => setChartGranularity(next)}
                    />
                  </div>
                </div>
                <div className="llm-panel-body" style={{ padding: '0 24px 22px' }}>
                  {chartLoading ? (
                    <div className="d-flex align-items-center justify-content-center" style={{ height: 250 }}>
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : chartError ? (
                    <div className="d-flex align-items-center justify-content-center" style={{ height: 250 }}>
                      <p className="ev-small-p-dark text-danger mb-0">Chart data currently unavailable</p>
                    </div>
                  ) : !chartHasActivity ? (
                    <div className="d-flex align-items-center justify-content-center" style={{ height: 250 }}>
                      <p className="ev-small-p-dark mb-0">
                        {chartGranularity === 'hour' && !startDateTime && !endDateTime ? `No agent runs in the last ${HOURLY_VIEW_HOURS} hours` : 'No agent runs yet'}
                      </p>
                    </div>
                  ) : (
                    <CallVolumeSparkline series={chartSeries} bucket={chartResult.bucket} timeZone={timezone} />
                  )}
                </div>
              </section>

              <section className="ad-panel">
                <div className="ad-panel__heading">
                  <div className="d-flex align-items-center gap-3">
                    <span className="ad-panel__heading-icon"><i className="bi bi-people" style={{ fontSize: '14px' }}></i></span>
                    <div>
                      <h2>Runs by user</h2>
                      <p>{dashboardScopeLabel}</p>
                    </div>
                  </div>
                </div>
                <div className="llm-panel-body" style={{ padding: '0 22px 20px' }}>
                  {userBreakdownLoading ? (
                    <div className="d-flex align-items-center justify-content-center" style={{ height: 250 }}>
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : userBreakdownError ? (
                    <div className="d-flex align-items-center justify-content-center" style={{ height: 250 }}>
                      <p className="ev-small-p-dark text-danger mb-0">User breakdown currently unavailable</p>
                    </div>
                  ) : (
                    <UserSunburst rows={userBreakdown} userLabel={userLabel} formatDateTime={formatDateTime} />
                  )}
                </div>
              </section>
              </div>

              <section className="ad-panel">
                <div className="ad-panel__heading">
                  <div className="d-flex align-items-center gap-3">
                    <span className="ad-panel__heading-icon"><i className="bi bi-list-ul" style={{ fontSize: '14px' }}></i></span>
                    <div>
                      <h2>{listView === 'sessions' ? 'Sessions' : 'Agent runs'}</h2>
                      <p>
                        {listView === 'sessions'
                          ? 'One row per execution — every agent run started by a single preset, selected-agents or single-agent request.'
                          : 'One row per agent run.'}
                      </p>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                  <div className="btn-group" role="group" aria-label="List view">
                    {[['runs', 'Agent runs'], ['sessions', 'Sessions']].map(([view, label]) => (
                      <button
                        key={view}
                        type="button"
                        className={`btn btn-sm ev-fs-11 ${listView === view ? 'ev-btn-lightblue-fill-round' : 'ev-btn-light-fill'}`}
                        onClick={() => switchListView(view)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  </div>
                </div>
                <div style={{ padding: '0 20px 20px' }}>
                {listView === 'runs' && sessionFilter && (
                  <div className="d-flex align-items-center gap-2 mb-2 ev-fs-11">
                    <span className="ev-light-reg">Showing runs from session</span>
                    <span className="ev-message-card1 ev-fs-11" title={sessionFilter}>{sessionFilter.slice(0, 8)}</span>
                    <button type="button" className="btn p-0 ev-fs-11 ev-link-accent" onClick={() => { setSessionFilter(''); setPage(1); }}>
                      Clear
                    </button>
                  </div>
                )}
                {listView === 'sessions' ? (
                <div className="table-container">
                  <table className="table-custom1 mt-0 overflow-hidden w-100">
                    <thead>
                      <tr>
                        <th className="w-15"><label>Started At</label></th>
                        <th className="w-20"><label>Preset</label></th>
                        <th className="w-15"><label>User</label></th>
                        <th className="w-10"><label>Agent runs</label></th>
                        <th className="w-10"><label>LLM calls</label></th>
                        <th className="w-10"><label>Tokens</label></th>
                        <th className="w-10"><label>Duration</label></th>
                        <th className="w-10"><label>Status</label></th>
                      </tr>
                    </thead>
                  </table>
                  <div className="scrollable-element dashboard-right-contentarea-table-height-1 pe-1">
                    <table className="table-custom1 overflow-hidden w-100">
                      <tbody>
                        {sessions.length === 0 ? (
                          <div className="w-100 text-center">
                            <p className="ev-small-p-blue mt-5">
                              {isProcessingPopupVisible ? 'Loading sessions' : 'No sessions yet'}
                            </p>
                          </div>
                        ) : (
                          sessions.map((sn) => (
                            <tr
                              key={sn.session_id}
                              onClick={() => showSessionRuns(sn.session_id)}
                              style={{ cursor: 'pointer' }}
                              title="Show the agent runs in this session"
                            >
                              <td className="align-top w-15">{formatDateTime(sn.started_at)}</td>
                              <td className="align-top w-20">
                                <div>
                                  <TrimmedText text={sn.template_name || (sn.template_id ? `Preset ${sn.template_id}` : '—')} maxLength={32} />
                                </div>
                                <div className="ev-fs-11 ev-light-reg">{workflowLabel(sn.pipeline)}</div>
                              </td>
                              <td className="align-top w-15">
                                <TrimmedText text={userLabel(sn.user_id)} maxLength={20} />
                              </td>
                              <td className="align-top w-10">
                                {formatNumber(sn.agent_runs)}
                                {sn.error_count > 0 && <span className="text-danger ev-fs-11"> ({sn.error_count} failed)</span>}
                              </td>
                              <td className="align-top w-10">{formatNumber(sn.total_llm_calls)}</td>
                              <td className="align-top w-10"><TokenCell value={sn.total_tokens} max={maxSessionTokens} /></td>
                              <td className="align-top w-10">{sn.duration_ms != null ? formatDuration(sn.duration_ms) : '—'}</td>
                              <td className="align-top w-10">
                                <StatusDot status={sn.status} />
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                ) : (
                <div className="table-container">
                  <table className="table-custom1 mt-0 overflow-hidden w-100">
                    <thead>
                      <tr>
                        <th className="w-20"><label>Started At</label></th>
                        <th className="w-20"><label>Agent / Preset</label></th>
                        <th className="w-20"><label>User</label></th>
                        <th className="w-15"><label>Status</label></th>
                        <th className="w-15"><label>Tokens</label></th>
                        <th className="w-10"><label>View</label></th>
                      </tr>
                    </thead>
                  </table>

                  <div className="scrollable-element dashboard-right-contentarea-table-height-1 pe-1">
                    <table className="table-custom1 overflow-hidden w-100">
                      <tbody>
                        {traces.length === 0 ? (
                          <div className="w-100 text-center">
                            <p className="ev-small-p-blue mt-5">
                              {isProcessingPopupVisible ? 'Loading LLM traces' : 'No LLM traces yet'}
                            </p>
                          </div>
                        ) : (
                          traces.map((t) => (
                            <tr key={t.id}>
                              <td className="align-top w-20">{formatDateTime(t.started_at)}</td>
                              <td className="align-top w-20">
                                <div>
                                  <TrimmedText text={t.agent_name || workflowLabel(t.pipeline)} maxLength={28} />
                                </div>
                                <div className="ev-fs-11 ev-light-reg">
                                  <TrimmedText text={t.template_name || workflowLabel(t.pipeline)} maxLength={32} />
                                </div>
                              </td>
                              <td className="align-top w-20">
                                <TrimmedText text={userLabel(t.user_id)} maxLength={20} />
                              </td>
                              <td className="align-top w-15">
                                <StatusDot status={t.status} />
                              </td>
                              <td className="align-top w-15"><TokenCell value={t.total_tokens} max={maxRunTokens} /></td>
                              <td className="align-top w-10">
                                <button
                                  type="button"
                                  className="btn ev-btn-skyblue-fill-round ev-radius-xsmall ev-btn-med pe-2 ps-2 smart-tooltip"
                                  onClick={() => openDetail(t)}
                                  data-tooltip="View details"
                                >
                                  <i className="bi bi-eye"></i>
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                )}

                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="ev-small-p-dark">
                    Showing {listView === 'sessions' ? sessions.length : traces.length} of {total} entries
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      className="ev-btn-light-fill"
                      disabled={page === 1}
                      onClick={() => setPage(page - 1)}
                    >
                      <i className="bi bi-chevron-left"></i>
                    </button>
                    <button className="ev-btn-light-fill" disabled>
                      Page {page} of {Math.max(Math.ceil(total / size), 1)}
                    </button>
                    <button
                      className="ev-btn-light-fill"
                      disabled={page >= Math.ceil(total / size)}
                      onClick={() => setPage(page + 1)}
                    >
                      <i className="bi bi-chevron-right"></i>
                    </button>
                  </div>
                  <div>
                    <PaginationControls
                      initialSize={size}
                      onSizeChange={(newSize) => { setSize(newSize); setPage(1); }}
                    />
                  </div>
                </div>
                </div>
              </section>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Portalled to body, above the fixed side nav (z-index 10000), which
          would otherwise sit on top of the dialog and its backdrop. */}
      {detailTrace && ReactDOM.createPortal(
        <div className="modal fade show d-block bg-dark bg-opacity-50" style={{ zIndex: 'var(--num-1000000000)' }}>
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-xl">
            <div className="modal-content llm-trace-detail" style={{ maxHeight: 'calc(100vh - 120px)' }}>
              <div className="modal-header">
                <p className="mb-0 ev-fs-15 ev-bold-med">
                  Trace Details
                  <span className={`${statusBadgeClass(detailTrace.status)} ev-fs-11 ms-2`}>
                    {statusLabel(detailTrace.status)}
                  </span>
                </p>
                <button type="button" className="btn-close" onClick={closeDetail}></button>
              </div>
              <div className="modal-body ev-fs-12 pb-4 scrollable-element" style={{ height: 'auto' }}>
                <div className="ev-card-2 mb-3">
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                    <span className="ev-small-p-dark ev-bold-reg">{workflowLabel(detailTrace.pipeline)}</span>
                    {detailTrace.agent_name && (
                      <span className="ev-message-card1 ev-fs-11">{detailTrace.agent_name}</span>
                    )}
                  </div>
                  <span className="ev-fs-12">
                    {formatDateTime(detailTrace.started_at)} &middot; {userLabel(detailTrace.user_id)}
                    {!detailLoading && detailModels.length > 0 && (
                      <>
                        {' '}&middot; Models:{' '}
                        <span title={detailModels.join(', ')}>{detailModels.map(shortModelId).join(', ')}</span>
                      </>
                    )}
                  </span>
                  {!detailLoading && (
                    <div className="ev-small-p-dark mt-2">
                      {formatDuration(detailTrace.latency_ms)} &middot; {formatNumber(detailTrace.total_tokens)} tokens &middot;{' '}
                      {llmCallCount} LLM call{llmCallCount === 1 ? '' : 's'} &middot;{' '}
                      {toolCallCount} tool call{toolCallCount === 1 ? '' : 's'}
                    </div>
                  )}
                </div>

                {/* Nothing below renders until the detail call settles — the
                    list row alone has no metadata or steps, so showing those
                    sections early flashed nulls and a "0 steps" count. */}
                {detailLoading ? (
                  <div className="d-flex flex-column align-items-center justify-content-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="ev-small-p-dark mt-2 mb-0">Loading trace detail...</p>
                  </div>
                ) : (
                  <>
                    {detailError && (
                      <p className="ev-fs-11 text-danger mb-3">
                        {detailError} &mdash; showing what is available from the list view.
                      </p>
                    )}

                    {detailTrace.status === 'ERROR' && (
                      <div className="mb-3">
                        <label className="ev-reg-p-dark mb-1 text-danger">Error Message</label>
                        <div className="ev-card-3">
                          <p className="ev-small-p-dark mb-0 text-danger">
                            {detailTrace.error_message || 'No error message provided.'}
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="mb-3">
                      <label className="ev-reg-p-dark mb-1">Details</label>
                      <div className="ev-card-3">
                        <div className="row g-2 ev-fs-12">
                          {[
                            ['Preset', detailTrace.template_name || runMeta.template_id || detailTrace.template_id],
                            ['Section', detailTrace.section_name || runMeta.section_id],
                            ['Mode', runModeLabel(runMeta)],
                            ['Feedback', runMeta.feedback_used ? 'Used' : null],
                            ...Object.entries(runMeta)
                              .filter(([k, v]) => !KNOWN_RUN_METADATA_KEYS.has(k) && v !== null && v !== undefined && v !== '')
                              .map(([k, v]) => [stepTypeLabel(k), typeof v === 'object' ? JSON.stringify(v) : String(v)])
                          ]
                            .filter(([, v]) => v !== null && v !== undefined && v !== '')
                            .map(([k, v]) => (
                              <div key={k} className={k === 'Preset' || k === 'Section' ? 'col-12 col-md-6' : 'col-6 col-md-3'}>
                                <div className="ev-light-reg">{k}</div>
                                <div className="ev-small-p-dark">{String(v)}</div>
                              </div>
                            ))}
                        </div>
                        {Array.isArray(detailTrace.tags) && detailTrace.tags.length > 0 && (
                          <div className="d-flex flex-wrap gap-1 mt-2">
                            {detailTrace.tags.map((tag) => (
                              <span key={tag} className="ev-message-card1 ev-fs-11">{tag}</span>
                            ))}
                          </div>
                        )}
                        {/* IDs for support/debugging — the session id groups
                            every agent run of one template execution. */}
                        <div className="d-flex flex-wrap gap-3 mt-2 ev-fs-12 ev-light-reg">
                          <span>Run {detailTrace.id}</span>
                          {detailTrace.session_id && (
                            <button
                              type="button"
                              className="btn p-0 ev-fs-12 ev-link-accent"
                              title={detailTrace.session_id}
                              onClick={() => showSessionRuns(detailTrace.session_id)}
                            >
                              Session {String(detailTrace.session_id).slice(0, 8)} &middot; view all its runs
                            </button>
                          )}
                          {detailTrace.trace_id && <span title={detailTrace.trace_id}>Trace {String(detailTrace.trace_id).slice(0, 8)}</span>}
                        </div>
                      </div>
                    </div>

                    {tokenSteps.length > 0 && (
                      <div className="mb-3">
                        <label className="ev-reg-p-dark mb-1">Tokens by LLM call</label>
                        <div className="ev-card-3">
                          <TokenRadial steps={tokenSteps} onSelectStep={openStep} />
                        </div>
                      </div>
                    )}

                    {/* One row per step (LLM, tool or embedding call). The bar
                        under each row is a timeline: its position and width
                        are that step's start and duration as a share of the
                        whole run. Full content comes from the S3 execution
                        log; without it only the Postgres step index is known,
                        so rows aren't expandable. */}
                    <div className="mb-3">
                      <label className="ev-reg-p-dark mb-1">
                        Steps <span className="ev-light-reg">({shownSteps.length})</span>
                      </label>
                      {!hasFullLog && shownSteps.length > 0 && (
                        <p className="ev-fs-12 ev-light-reg mb-2">
                          Full prompts and outputs aren&apos;t available for this run — only the step index.
                        </p>
                      )}
                      {shownSteps.length === 0 ? (
                        <p className="ev-small-p-dark mb-0">No steps recorded for this run.</p>
                      ) : (
                        shownSteps.map((s, i) => {
                          const display = stepDisplay(s);
                          const llm = isLlmStep(s);
                          const failed = s.status === 'ERROR';
                          // Timeline column: where in the run this step sat,
                          // and for how long. Tool calls are drawn lighter
                          // than model calls; a failed step is red.
                          const barColor = failed ? '#dc3545' : (llm ? '#6320ff' : '#c4b8d4');
                          const header = (
                            <div className="d-flex align-items-center gap-3">
                              <span
                                className="ev-fs-12 ev-light-reg text-end flex-shrink-0"
                                style={{ width: 60, fontVariantNumeric: 'tabular-nums' }}
                                title="Time since the run started"
                              >
                                {formatOffset(stepBars[i].offsetMs)}
                              </span>
                              <span className="ev-small-p-dark flex-grow-1" style={{ minWidth: 0 }}>
                                {display.label}
                                <span className="ev-light-reg">
                                  {llm && s.model_id ? <span title={s.model_id}> &middot; {shortModelId(s.model_id)}</span> : null}
                                  {!llm ? ' · tool' : null}
                                  {!llm && s.chain_number ? ` · during answer ${s.chain_number}` : null}
                                </span>
                              </span>
                              <div
                                className="position-relative flex-shrink-0 d-none d-md-block"
                                style={{ width: '28%', height: 4, borderRadius: 2, background: 'var(--color8, #eee6fa)' }}
                                title={`${formatOffset(stepBars[i].offsetMs)} → ${formatDuration(s.latency_ms)}`}
                              >
                                <div
                                  style={{
                                    position: 'absolute', top: 0, bottom: 0, borderRadius: 2, background: barColor,
                                    left: `${stepBars[i].leftPct}%`, width: `${stepBars[i].widthPct}%`
                                  }}
                                ></div>
                              </div>
                              <span className="ev-fs-12 ev-light-reg text-nowrap text-end flex-shrink-0" style={{ width: 170 }}>
                                {failed && <span className="text-danger">Error &middot; </span>}
                                {formatDuration(s.latency_ms)}
                                {s.total_tokens != null ? <> &middot; {formatNumber(s.total_tokens)} tok</> : null}
                                {hasFullLog && <i className="bi bi-chevron-down ms-2"></i>}
                              </span>
                            </div>
                          );
                          if (!hasFullLog) {
                            return (
                              <div key={s.id ?? i} className={`py-2 border-bottom${failed ? ' border-danger' : ''}`}>
                                {header}
                              </div>
                            );
                          }
                          return (
                            <details key={i} id={`llm-step-${i}`} className={`py-2 border-bottom${failed ? ' border-danger' : ''}`}>
                              <summary style={{ cursor: 'pointer', listStyle: 'none', display: 'block' }}>{header}</summary>
                              <StepContent step={s} />
                            </details>
                          );
                        })
                      )}
                    </div>
                  </>
                )}

              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary ev-btn-reg" onClick={closeDetail}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {isProcessingPopupVisible && <ProcessingPopup message="Loading LLM traces" />}

      {isErrorPopupVisible && (
        <ErrorPopup onClose={() => setIsErrorPopupVisible(false)} message={popupErrorMessage} />
      )}
    </div>
  );
};

export default LlmTraces;